import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { FileText } from "lucide-react";
import { useLocation } from "react-router-dom";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { useDeepLinkScroll } from "../hooks/useDeepLinkScroll";
import { theme, card, emptyFrame, floatingSheet, btnPrimary, btnSecondary, inputStyle, type as text, pageHeading, pageSubtitle } from "../theme";
import { api } from "../api/client";
import { EntityTagsRow } from "../components/EntityTags";
import { DetailFields, DetailSection, EditButton, RecordActions } from "../components/RecordView";
import { useCurrentUser } from "../auth/useCurrentUser";
import { fmtDate, fmtDateTime } from "../lib/format";
import { toDateInput, dateInputToIso, fmtDateOnly, todayInput } from "../lib/dateOnly";
import { riskLabel } from "../components/RelatedRecords";
import { PlaybookBar } from "../components/PlaybookBar";
import { NextSteps, type NextStepItem } from "../components/NextSteps";
import { PortalLink } from "../components/PortalLink";
import { dataCategoryLabel, hasUnspecifiedSpecialCategory } from "../lib/dataCategories";
import { DataCategoryPicker } from "../components/DataCategoryPicker";
import { DsrDataChecklist, checklistSummary, type ChecklistReadiness } from "../components/DsrDataChecklist";
import { DpiaCriteriaFields, DpiaScreeningSummary, RopaDpiaScreeningFields } from "../components/DpiaScreening";
import { WP248_CRITERIA, ART35_3_CASES, sanitiseCriteriaRecord, unansweredCriteria, type DpiaCriteriaRecord, type DpiaCriterionResult, type DpiaLevel } from "../lib/dpiaCriteria";
import { statutoryRightsDeadline, pausedMs, countedPauses, type RightsLawClock } from "../lib/rightsDeadline";
import { COUNTRIES } from "../data/countriesData";
import {
  AgreementsTab, BreachLawFields, DpIdentityPanel, DpiaLawFields, DsrLawFields, RopaParticularsFields,
  breachLawFrom, breachLawPayload, dpiaLawFrom, dpiaLawPayload, dsrLawFrom, dsrLawPayload, ropaParticularsDetail,
  ropaParticularsFrom, ropaParticularsPayload, useAgreementOptions, useReloadableAgreementOptions,
  type BreachLawForm, type DpiaLawForm, type DsrLawForm, type RopaParticularsForm,
} from "../components/DpRegisterFields";
import { LAWFUL_BASES, REQUEST_TYPE_LABELS } from "../lib/dpParticulars";
import { RopaProcessorAgreements } from "../components/RopaProcessorAgreements";

// ── Types ──────────────────────────────────────────────────────────────────────

interface RopaEntry {
  id: string;
  name: string;
  purpose: string;
  controllerProcessor: string;
  dataCategories: string[];
  lawfulBases: string[];
  subjectCategories: string[];
  retentionPeriod: string | null;
  recipients: string[];
  thirdCountryTransfer: boolean;
  transferSafeguards: string | null;
  notes: string | null;
  // The API returns the linked DPIA as `dpia`; the page reads the two fields it needs.
  dpia?: { id: string; status: string; residualRisk: string } | null;
  dpiaId: string | null;
  dpiaStatus: string | null;
  // Art. 30(1)(d) / (g) as links: the suppliers processing for it and the assets holding the data.
  processors?: LinkedRow[];
  systems?: LinkedRow[];
  // Structured retention, transfer destinations and the large-scale flag (lib/dataProtectionLinks.ts).
  retentionValue?: number | null;
  retentionUnit?: string | null;
  retentionTrigger?: string | null;
  retentionReviewAt?: string | null;
  transferCountries?: string[];
  largeScale?: boolean;
  // DPIA screening (backend lib/dpiaCriteria.ts).
  dpiaCriteria?: string[];
  dpiaMandatoryCases?: string[];
  dpiaScreenedAt?: string | null;
  dpiaNotRequiredReason?: string | null;
  monitorsWorkers?: boolean;
  /** The row read against the registers linked to it — computed by the API, never edited. */
  linkage?: RopaLinkage | null;
  discoveredLocations?: Array<{ id: string; location: string; locationKind: string; severity: string; status: string; firstSeenAt: string; asset: LinkedRow | null }>;
  aiSystems?: Array<{ id: string; name: string; riskClass: string }>;
  // Law review package F particulars (backend lib/ropaParticulars.ts), as the API stores them.
  controllerName?: string | null;
  controllerContact?: string | null;
  securityMeasures?: string | null;
  specialCategoryConditions?: string[];
  criminalDataBasis?: string | null;
  ukSchedule1Conditions?: string[];
  transferMechanism?: string | null;
  agreements?: Array<{ id: string; kind: string; title: string; counterparty: string; status: string }>;
  agreementIds?: string[];
}

type DpaStanding = "in_force" | "terms_missing" | "terms_unanswered" | "no_agreement";
interface RopaLinkage {
  processors: Array<{ supplierId: string; name: string; isDataProcessor: boolean; standing: DpaStanding; returnOrDeletion: "yes" | "no" | "unanswered"; transferSafeguards: "yes" | "no" | "unanswered"; dataLocations: string[] }>;
  transfers: { undeclared: Array<{ supplierId: string; supplier: string; location: string }>; noSafeguard: boolean; assessed: boolean };
  dpia: {
    level: DpiaLevel; reasons: string[]; covered: boolean; screened: boolean; notRequired: boolean; metCount: number;
    criteria: DpiaCriterionResult[]; cases: Array<{ id: string; label: string; derived: boolean }>;
  };
  retention: { label: string | null; heldPast: Array<{ findingId: string; location: string; since: string }>; mayHoldPast?: Array<{ findingId: string; location: string; since: string }>; reviewOverdue: boolean; processorsWithoutReturnOrDeletion: Array<{ supplierId: string; name: string }> };
}
interface DpAttentionItem { id: string; type: string; title: string; description: string; href: string; severity: "high" | "medium" | "low" }

const DPA_STANDING: Record<DpaStanding, { label: string; colors: { bg: string; text: string } }> = {
  in_force:         { label: "Processor terms in force", colors: { bg: "var(--c-successBg)", text: "var(--c-successText)" } },
  terms_missing:    { label: "Agreement lacks processor terms", colors: { bg: "var(--c-dangerBg)", text: "var(--c-dangerText)" } },
  terms_unanswered: { label: "Processor terms not checked", colors: { bg: "var(--c-warningBg)", text: "var(--c-warningText)" } },
  no_agreement:     { label: "No agreement in force", colors: { bg: "var(--c-dangerBg)", text: "var(--c-dangerText)" } },
};

const RETENTION_UNITS = ["days", "months", "years"];
const RETENTION_TRIGGERS = [
  { id: "collection", label: "From collection" },
  { id: "last_activity", label: "From the last activity" },
  { id: "relationship_end", label: "From the end of the relationship" },
  { id: "statutory", label: "As the law sets it" },
  { id: "other", label: "From another event" },
];

/** A linked supplier or asset as the registers return it. */
interface LinkedRow { id: string; name: string }

interface DsrEntry {
  id: string;
  type: string;
  requestorName: string;
  requestorEmail: string;
  channel: string;
  receivedAt: string;
  dueAt: string;
  status: string;
  notes: string | null;
  refusalReason?: string | null;
  extended: boolean;
  /** Why the extension was claimed and when the requester was told (Art. 12(3)). */
  extensionReason?: string | null;
  requesterInformedAt?: string | null;
  /** Stops of the clock while identity, clarification or a fee was awaited (UK GDPR Art. 12A). */
  clockPauses?: Array<{ reason: string; from: string; to: string | null }> | null;
  completedAt?: string | null;
  closedAt?: string | null;
  // The processing activities and systems the request reaches, and what that means to action.
  ropaEntryIds?: string[];
  systemIds?: string[];
  actionList?: {
    activities: LinkedRow[];
    systems: Array<LinkedRow & { via: string[] }>;
    processors: Array<LinkedRow & { via: string[] }>;
  };
  /** How far the locating checklist has got (lib/dsrDataLocation.ts on the server). */
  checklist?: ChecklistReadiness;
  // Law review package F (backend lib/rightsRequests.ts): Art. 19 and the words beside the extension reason.
  recipientsNotified?: string | null;
  recipientsNotifiedNote?: string | null;
  extensionNote?: string | null;
}

interface DpiaEntry {
  id: string;
  title: string;
  ropaEntryId: string | null;
  ropaEntryName: string | null;
  description: string | null;
  status: string;
  necessity: string | null;
  risks: string | null;
  mitigations: string | null;
  residualRisk: string | null;
  dpoConsulted: boolean;
  dpoOpinion: string | null;
  linkedRiskIds?: string[];
  suppliers?: LinkedRow[];
  systems?: LinkedRow[];
  aiSystems?: Array<LinkedRow & { riskClass: string }>;
  submittedById?: string | null;
  submittedByName?: string | null;
  submittedAt?: string | null;
  approvedByName?: string | null;
  approvedAt?: string | null;
  /** The assessment against the nine WP248 criteria and the Art. 35(3) cases. */
  criteria?: DpiaCriteriaRecord;
  mandatoryCases?: string[];
  /** Next review. Date-only (UTC midnight). */
  reviewAt?: string | null;
  // Law review package F (backend lib/dpiaWorkflow.ts): Art. 35(2), 35(9) and the Art. 36 consultation.
  dpoAdviceFollowed?: boolean | null;
  dpoDisagreementReason?: string | null;
  subjectViewsSought?: boolean | null;
  subjectViews?: string | null;
  subjectViewsNotSoughtReason?: string | null;
  priorConsultationAt?: string | null;
  priorConsultationAuthority?: string | null;
  priorConsultationExtended?: boolean;
  priorConsultationReplyAt?: string | null;
  priorConsultationReply?: string | null;
}

/** What a DPIA still lacks before it can be approved (mirror of backend lib/dpiaWorkflow.ts dpiaMissingForApproval). */
function dpiaMissingForApproval(d: DpiaEntry): string[] {
  const has = (v: string | null | undefined) => !!v && v.trim().length > 0;
  return [
    has(d.description) ? null : "description of the processing",
    has(d.necessity) ? null : "necessity and proportionality",
    has(d.risks) || (d.linkedRiskIds ?? []).length > 0 ? null : "risks",
    has(d.mitigations) ? null : "mitigations",
    d.residualRisk && d.residualRisk !== "unknown" ? null : "residual risk",
    (d.subjectViewsSought === true && has(d.subjectViews)) || (d.subjectViewsSought === false && has(d.subjectViewsNotSoughtReason)) ? null : "the views of data subjects, or why they were not sought",
  ].filter((x): x is string => !!x);
}

/** Art. 36: where the consultation stands (mirror of backend lib/dpiaWorkflow.ts priorConsultationStanding). */
function priorConsultationText(d: DpiaEntry): string | null {
  if (d.residualRisk !== "high") return null;
  if (!d.priorConsultationAt || !d.priorConsultationAuthority) return "Not yet consulted: required before the processing (Art. 36(1)).";
  const weeks = 8 + (d.priorConsultationExtended ? 6 : 0);
  const ends = new Date(new Date(d.priorConsultationAt).getTime() + weeks * 7 * 86_400_000);
  if (d.priorConsultationReplyAt) return `${d.priorConsultationAuthority} consulted ${fmtDate(d.priorConsultationAt)}; replied ${fmtDate(d.priorConsultationReplyAt)}.`;
  return `${d.priorConsultationAuthority} consulted ${fmtDate(d.priorConsultationAt)}; its period ${ends.getTime() <= Date.now() ? "ended" : "ends"} ${fmtDate(ends.toISOString())} (Art. 36(2)).`;
}

// ── What's missing, per register ──────────────────────────────────────────
// What a complete record holds under the data protection laws: the Article 30 record, a request
// answered on its clock, an Article 35 assessment, an Article 33 / 34 breach record.

const filled = (v: string | null | undefined) => !!v && v.trim().length > 0;

function ropaSteps(e: RopaEntry): NextStepItem[] {
  const l = e.linkage;
  const processorsUnsettled = (l?.processors ?? []).filter((p) => p.standing !== "in_force").length;
  return [
    { done: filled(e.purpose), label: "State the purpose of the processing" },
    { done: e.lawfulBases.length > 0, label: "Record the lawful basis", hint: "Article 6, and Article 9 for special category data." },
    { done: e.subjectCategories.length > 0, label: "List the categories of people" },
    { done: e.dataCategories.length > 0, label: "List the categories of personal data" },
    { done: e.recipients.length > 0 || (e.processors ?? []).length > 0, label: "Name the recipients and processors" },
    { done: filled(e.retentionPeriod) || e.retentionValue != null, label: "Set how long the data is kept" },
    ...(e.thirdCountryTransfer || (e.transferCountries ?? []).length > 0 ? [{
      done: filled(e.transferSafeguards) && !l?.transfers.noSafeguard, label: "Record the safeguard for transfers abroad",
      urgent: !!l?.transfers.noSafeguard,
    }] : []),
    ...(l && (e.processors ?? []).length > 0 ? [{
      done: processorsUnsettled === 0, label: "Have processor terms in force with every processor",
      hint: processorsUnsettled ? `${processorsUnsettled} processor${processorsUnsettled === 1 ? "" : "s"} without processor terms confirmed (Article 28).` : undefined,
    }] : []),
    ...(hasUnspecifiedSpecialCategory(e.dataCategories) ? [{
      done: false, label: "Say which kinds of special category data it involves",
      hint: "Recorded before special category data was split into its nine kinds (Article 9(1)).",
    }] : []),
    { done: !!e.dpiaScreenedAt, label: "Screen it for a DPIA against the nine WP248 criteria" },
    ...(l && l.dpia.level !== "none" ? [{
      done: l.dpia.covered || !!e.dpiaId || l.dpia.notRequired || (l.dpia.level === "consider" && l.dpia.screened),
      label: l.dpia.level === "mandatory" ? "Carry out the DPIA this processing needs"
        : l.dpia.level === "likely" ? "Carry out a DPIA, or record why none is needed"
        : "Decide whether this processing needs a DPIA",
      hint: l.dpia.reasons.join("; ") || undefined,
      urgent: (l.dpia.level === "mandatory" || (l.dpia.level === "likely" && !l.dpia.notRequired)) && !l.dpia.covered && !e.dpiaId,
    }] : []),
    ...(l?.retention.reviewOverdue ? [{ done: false, label: "Review the retention period (the review date has passed)", urgent: true }] : []),
  ];
}

function dsrSteps(e: DsrEntry): NextStepItem[] {
  const open = !CLOSED_DSR.includes(e.status);
  const overdue = open && e.dueAt.slice(0, 10) < todayInput();
  return [
    { done: (e.ropaEntryIds ?? []).length > 0 || (e.systemIds ?? []).length > 0, label: "Link the processing activities and systems it reaches", hint: "So nothing is missed when searching or changing the data." },
    { done: !!e.checklist?.ready || (!open && !e.checklist?.built), label: "Find and deal with every place the person's data is",
      hint: checklistSummary(e.checklist) ?? "Build the checklist under Where the person's data is." },
    { done: !open, label: "Respond to the request", hint: overdue ? "The statutory deadline has passed." : `By ${fmtDateOnly(e.dueAt)}.`, urgent: overdue },
    ...(e.status === "refused" ? [{ done: filled(e.refusalReason), label: "Record why it was refused", hint: "The person must be told the reason and how to complain." }] : []),
  ];
}

function dpiaSteps(d: DpiaEntry): NextStepItem[] {
  if (d.status === "approved") return [];
  const missing = dpiaMissingForApproval(d);
  return [
    { done: filled(d.description), label: "Describe the processing" },
    { done: !!d.ropaEntryId, label: "Link the processing activity in the RoPA" },
    { done: unansweredCriteria(d.criteria ?? {}).length === 0, label: "Answer each of the nine WP248 criteria",
      hint: unansweredCriteria(d.criteria ?? {}).length ? `${unansweredCriteria(d.criteria ?? {}).length} of 9 not answered.` : undefined },
    { done: !missing.includes("necessity and proportionality"), label: "Assess necessity and proportionality" },
    { done: !missing.includes("risks"), label: "Identify the risks to the people affected" },
    { done: !missing.includes("mitigations"), label: "Record the measures that address them" },
    { done: !missing.includes("residual risk"), label: "Rate the residual risk", hint: d.residualRisk === "high" ? "High residual risk: consult the supervisory authority before processing (Article 36)." : undefined },
    { done: d.dpoConsulted && filled(d.dpoOpinion), label: "Record the DPO's advice", hint: "Article 35(2), where a DPO is designated." },
    ...(d.dpoConsulted ? [{ done: d.dpoAdviceFollowed === true || (d.dpoAdviceFollowed === false && filled(d.dpoDisagreementReason)), label: "Record whether the DPO's advice was followed, and if not why" }] : []),
    { done: !missing.includes("the views of data subjects, or why they were not sought"), label: "Record the views of data subjects, or why they were not sought", hint: "Article 35(9)." },
    ...(d.residualRisk === "high" ? [{ done: !!d.priorConsultationReplyAt, label: "Consult the supervisory authority before processing", hint: priorConsultationText(d) ?? undefined, urgent: !d.priorConsultationAt }] : []),
    { done: false, label: "Approve the assessment", hint: missing.length ? "Once the steps above are done." : undefined },
  ];
}

function breachSteps(e: BreachEntry, authorityHours: number): NextStepItem[] {
  if (e.status === "closed") return [];
  const severe = e.severity === "high" || e.severity === "critical";
  const role = e.orgRole ?? "controller";
  const asController = role !== "processor";
  return [
    { done: filled(e.description), label: "Describe what happened" },
    { done: e.breachTypes.length > 0, label: "Say what kind of breach it was", hint: "Confidentiality, integrity or availability." },
    { done: e.dataCategories.length > 0, label: "Record the categories of data involved" },
    { done: e.affectedCount != null, label: "Estimate how many people are affected" },
    ...(role !== "controller" ? [{
      done: !!e.controllerNotifiedAt, label: "Tell the controller", urgent: !e.controllerNotifiedAt,
      hint: "As processor, without undue delay (Article 33(2)); the controller decides whether the authority is told.",
    }] : []),
    ...(asController && e.notifiedDpaRequired ? [
      { done: (e.subjectCategories ?? []).length > 0 && e.approxRecords != null && filled(e.dpoContact) && filled(e.likelyConsequences), label: "Complete what the notification must describe", hint: "Categories and numbers of people and records, a contact point and the likely consequences (Article 33(3))." },
      {
        done: !!e.notifiedDpaAt || !!e.authorityNotice, label: "Notify the supervisory authority",
        hint: `Within ${authorityHours} hours of becoming aware (Article 33).`, urgent: isAuthorityOverdue(e, authorityHours),
      },
    ] : []),
    ...(asController && !e.notifiedDpaRequired ? [{ done: filled(e.notNotifiedReason), label: "Record why the authority is not being told", hint: "Article 33(5)." }] : []),
    ...(asController && severe ? [{ done: !!e.notifiedSubjectsAt || !!e.subjectsExemption, label: "Tell the people affected, or record the exemption relied on", hint: "Required when the breach is likely to put them at high risk (Article 34), unless an Article 34(3) exemption applies." }] : []),
    { done: filled(e.remediation), label: "Record the containment and remediation" },
    { done: (e.linkedIncidents ?? []).length > 0, label: "Link the security incident it came from" },
  ];
}

interface BreachEntry {
  id: string;
  title: string;
  description: string | null;
  discoveredAt: string;
  severity: string;
  status: string;
  notifiedDpaRequired: boolean;
  notifiedDpaAt: string | null;
  breachTypes: string[];
  dataCategories: string[];
  affectedCount: number | null;
  remediation: string | null;
  notes: string | null;
  notifiedSubjectsAt: string | null;
  supplierId?: string | null;
  supplier?: LinkedRow | null;
  assets?: LinkedRow[];
  linkedRiskId?: string | null;
  ropaEntries?: LinkedRow[];
  /** The cyber incidents this breach is recorded against (SecurityIncident.breachId). */
  linkedIncidents?: LinkedIncident[];
  /**
   * Whether the supervisory authority was told by any route: the breach's own date, or a sent
   * notification duty / regulatory submission on a linked incident. A breach with one is not overdue.
   */
  authorityNotice?: AuthorityNotice | null;
  /** The authorities the breach answered to when it was reported (null on older breaches: today's list applies). */
  authoritiesAtReport?: BreachAuthority[] | null;
  /** Which authority was told, and when — one row each, final once recorded. */
  authorityNotices?: Array<{ authority: string; regulation: string; notifiedAt: string }>;
  // Law review package F (backend lib/breachContent.ts).
  orgRole?: string;
  controllerNotifiedAt?: string | null;
  affectedControllers?: string[];
  notNotifiedReason?: string | null;
  subjectCategories?: string[];
  approxRecords?: number | null;
  dpoContact?: string | null;
  likelyConsequences?: string | null;
  delayReasons?: string | null;
  subjectsExemption?: string | null;
  subjectsExemptionNote?: string | null;
  agreementIds?: string[];
}

/** One supervisory authority a breach answers to, with its own clock (backend lib/breachNotifications.ts). */
interface BreachAuthority { authority: string; regulation: string; hours: number | null; label: string | null }

/** The authorities a breach answers to: those kept when it was reported, or today's for an older breach. */
function breachAuthorities(entry: BreachEntry, clocks: DpContextData["clocks"], phrase: string): BreachAuthority[] {
  if (entry.authoritiesAtReport && entry.authoritiesAtReport.length > 0) return entry.authoritiesAtReport;
  if (clocks.authorities.length === 0) {
    const c = clocks.breachAuthority;
    return [{ authority: phrase, regulation: c?.regulation ?? "", hours: c?.value ?? null, label: c?.label ?? null }];
  }
  const own = clocks.authorityBreachClocks ?? {};
  return clocks.authorities.map((a) => {
    const c = a.authority in own ? own[a.authority] : clocks.breachAuthority;
    return { authority: a.authority, regulation: a.regulation, hours: c?.value ?? null, label: c?.label ?? null };
  });
}

/** "the ICO and the DPC" — the authorities recorded as notified on this breach. */
function notifiedAuthorityNames(entry: BreachEntry): string | null {
  const names = (entry.authorityNotices ?? []).map((n) => authorityNoun(n.authority));
  if (names.length === 0) return null;
  return names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

interface LinkedIncident { id: string; displayId: number | null; title: string; status: string; severity: string }

interface AuthorityNotice {
  at: string;
  via: "breach" | "notification" | "submission";
  authority: string | null;
  reference: string | null;
  incidentRef: string | null;
}

const incidentRef = (i: { displayId: number | null }) => (i.displayId != null ? `INC-${String(i.displayId).padStart(3, "0")}` : "INC");

/** The authority was told through a linked incident, but the breach does not carry the date yet. */
const toldViaIncident = (b: BreachEntry): AuthorityNotice | null =>
  !b.notifiedDpaAt && b.authorityNotice && b.authorityNotice.via !== "breach" ? b.authorityNotice : null;

/** "ICO told 3 Oct 2026, 14:05 through INC-004 (the notification), reference ABC". */
function authorityNoticeText(n: AuthorityNotice, fallback: string): string {
  const who = n.authority ? authorityNoun(n.authority) : fallback;
  const route = n.via === "submission" ? "a regulatory submission" : "the notification";
  return `${who} told ${fmtDateTime(n.at)} through ${n.incidentRef ?? "the linked incident"} (${route})${n.reference ? `, reference ${n.reference}` : ""}`;
}

/** The linked incidents as links to the incident register. */
function IncidentLinks({ incidents }: { incidents: LinkedIncident[] }) {
  return (
    <span style={{ display: "inline-flex", gap: 8, flexWrap: "wrap" }}>
      {incidents.map((i) => (
        <PortalLink key={i.id} to={`/resilience/incidents?incident=${encodeURIComponent(i.id)}`} onClick={(e) => e.stopPropagation()}
          title={`${humanise(i.status)}, ${i.severity} severity`} style={{ color: "var(--c-accentText)", textDecoration: "none" }}>
          <span style={text.ref}>{incidentRef(i)}</span> {i.title}
        </PortalLink>
      ))}
    </span>
  );
}

/**
 * Once the authority or the people affected have been told, that date is the record of it: the
 * API refuses to move or clear it and refuses to delete the breach (gdpr.routes.ts
 * breachNotificationLock).
 */
const breachNotified = (b: BreachEntry) => !!b.notifiedDpaAt || !!b.notifiedSubjectsAt;

type GdprTab = "ropa" | "dsr" | "dpia" | "breaches" | "agreements";

// ── Law context ────────────────────────────────────────────────────────────────
// GET /gdpr/context: the data protection laws this organisation is under, whether
// any of them requires a record of processing (with the threshold engine's verdict),
// and the statutory clocks the DSR and breach tabs compute from. The same per-law
// table drives the generated documents, so the page and the documents agree.

interface ClockSource { value: number; regulation: string; label: string; months?: number | null }

interface RopaDuty {
  regulation: string;
  controlId: string | null;
  basis: string;
  status: "required" | "conditional" | "exempt" | "unknown" | "applicable" | "not_applicable";
  detail: string | null;
}

export interface DpContextData {
  regulations: string[];
  module: { enabled: boolean; label: string };
  ropa: { required: boolean; duties: RopaDuty[] };
  clocks: {
    rights: ClockSource;
    rightsExtension: ClockSource | null;
    /** Each law in effect with its own rights period and extension; deadlines are computed from these. */
    rightsLaws?: RightsLawClock[];
    breachAuthority: ClockSource | null;
    breachSubjects: ClockSource | null;
    authorities: Array<{ regulation: string; authority: string }>;
    /** Each authority's own breach clock, keyed by its `authorities` name. */
    authorityBreachClocks?: Record<string, ClockSource | null>;
  };
  authorityPhrase: string;
  /**
   * A UK data protection law is in scope (backend lib/dataProtectionModule.ts): Art. 6(1)(ea), Schedule 1, the
   * ICO's Art. 35(4) list, and any AI system counting toward DPIA criterion 8.
   */
  ukLaw?: boolean;
  /** The request types the laws provide, with each one's clock (backend lib/rightsRequests.ts). */
  requestTypes?: Array<{ id: string; clock: { kind: string; label?: string } }>;
  /** UK GDPR Art. 12A applies (the clock may stop for identity, a fee or clarification of an access request). */
  art12A?: boolean;
}

// Until the context loads (or if it fails), fall back to the GDPR working standard
// the page has always used, so nothing flashes and nothing is wrong for a GDPR org.
const DEFAULT_DP_CONTEXT: DpContextData = {
  regulations: [],
  module: { enabled: true, label: "Data Protection" },
  ropa: { required: true, duties: [] },
  clocks: {
    rights: { value: 30, regulation: "", label: "30 days" },
    rightsExtension: { value: 60, regulation: "", label: "60 days" },
    breachAuthority: { value: 72, regulation: "", label: "72 hours" },
    breachSubjects: null,
    authorities: [],
  },
  authorityPhrase: "the supervisory authority",
};

const DpContext = createContext<DpContextData>(DEFAULT_DP_CONTEXT);
const useDp = () => useContext(DpContext);

/** "the ICO" → "ICO", "the JOIC and the ICO" → "JOIC and ICO", for labels such as "ICO notified". */
function authorityNoun(phrase: string): string {
  const s = phrase.replace(/^the /i, "").replace(/ the /g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "72 hours" / "four weeks under Jersey Data Protection Law 2018" — never parenthesised, as callers often are. */
/** "72 hours under BDSG"; `after` goes before the law ("72 hours of becoming aware under BDSG"). */
function clockLabel(c: ClockSource | null, fallback: string, after?: string): string {
  const label = after ? `${c ? c.label : fallback} ${after}` : (c ? c.label : fallback);
  return c?.regulation ? `${label} under ${c.regulation}` : label;
}

const ROPA_STATUS_STYLE: Record<RopaDuty["status"], { label: string; bg: string; fg: string }> = {
  required:       { label: "Required",        bg: "var(--c-dangerBg)",  fg: "var(--c-dangerText)" },
  conditional:    { label: "Conditional",     bg: "var(--c-warningBg)", fg: "var(--c-warningText)" },
  applicable:     { label: "Applies",         bg: "var(--c-accentBg)",  fg: "var(--c-accentText)" },
  unknown:        { label: "Check threshold", bg: "var(--c-warningBg)", fg: "var(--c-warningText)" },
  exempt:         { label: "Exempt",          bg: "var(--c-successBg)", fg: "var(--c-successText)" },
  not_applicable: { label: "Not applicable",  bg: "var(--c-panel)",     fg: "var(--c-muted)" },
};

/** The laws that require this register, each with the threshold engine's verdict for this organisation. */
function RopaDutiesBanner({ duties }: { duties: RopaDuty[] }) {
  if (duties.length === 0) return null;
  return (
    <div style={{ ...card, marginBottom: 16, padding: "12px 16px" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>
        Record-keeping duty under the laws that apply to you
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {duties.map((d) => {
          const st = ROPA_STATUS_STYLE[d.status] ?? ROPA_STATUS_STYLE.applicable;
          // The verdict's headline often opens with the pill's own word ("Required — 300 staff");
          // only what it adds is shown under the pill.
          const rest = (d.detail ?? "").replace(new RegExp(String.raw`^${st.label}\b\s*(—\s*)?`, "i"), "").trim();
          const extra = rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : "";
          return (
            <div key={d.regulation} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 12.5, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 2, background: st.bg, color: st.fg, whiteSpace: "nowrap" }}>{st.label}</span>
              <span style={{ color: theme.color.text, fontWeight: 600 }}>{d.regulation}</span>
              <span style={{ color: theme.color.muted }}>{d.basis}</span>
              {extra && <span style={{ color: theme.color.faint, flexBasis: "100%" }}>{extra}</span>}
            </div>
          );
        })}
      </div>
      {duties.some((d) => d.status === "exempt") && (
        <div style={{ fontSize: 11.5, color: theme.color.faint, marginTop: 8 }}>
          An exemption removes the statutory duty, not the value of the record: the register still feeds your privacy notices, DPIAs and supplier reviews.
        </div>
      )}
    </div>
  );
}

// ── Constants ──────────────────────────────────────────────────────────────────


const SUBJECT_CATEGORIES = [
  { id: "employees",         label: "Employees" },
  { id: "customers",         label: "Customers" },
  { id: "website_visitors",  label: "Website Visitors" },
  { id: "suppliers",         label: "Suppliers" },
  { id: "job_applicants",    label: "Job Applicants" },
  { id: "children",          label: "Children" },
  { id: "patients",          label: "Patients" },
  { id: "vulnerable_adults", label: "Vulnerable Adults" },
];

const BREACH_TYPES = [
  { id: "confidentiality",  label: "Confidentiality" },
  { id: "integrity",        label: "Integrity" },
  { id: "availability",     label: "Availability" },
];

const DSR_TYPES = ["access", "erasure", "rectification", "portability", "objection", "restriction", "automated_decision"];
const DSR_CHANNELS = ["email", "web_form", "post", "verbal", "other"];

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  open:         { bg: "var(--c-accentBg)",  text: "var(--c-accentText)" },
  in_progress:  { bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
  completed:    { bg: "var(--c-successBg)", text: "var(--c-successText)" },
  refused:      { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
  withdrawn:    { bg: "var(--c-faint)",     text: "var(--c-muted)" },
  draft:        { bg: "var(--c-faint)",     text: "var(--c-muted)" },
  in_review:    { bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
  approved:     { bg: "var(--c-successBg)", text: "var(--c-successText)" },
  rejected:     { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
  reported_to_dpa: { bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
  closed:       { bg: "var(--c-successBg)", text: "var(--c-successText)" },
};

const SEVERITY_COLORS: Record<string, { bg: string; text: string }> = {
  low:      { bg: "var(--c-successBg)", text: "var(--c-successText)" },
  medium:   { bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
  high:     { bg: "var(--c-riskHighBg)", text: "var(--c-riskHighText)" },
  critical: { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
};

const RISK_COLORS: Record<string, { bg: string; text: string }> = {
  low:      { bg: "var(--c-successBg)", text: "var(--c-successText)" },
  medium:   { bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
  high:     { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
  unknown:  { bg: "var(--c-faint)",     text: "var(--c-muted)" },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function Pill({ label, colors }: { label: string; colors: { bg: string; text: string } }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 3,
      fontSize: 11, fontWeight: 600, background: colors.bg, color: colors.text, whiteSpace: "nowrap",
      textTransform: "capitalize",
    }}>
      {label.replace(/_/g, " ")}
    </span>
  );
}

/** Writes to this register need privacy:write (the router's requireWritePermission). */
const CanEdit = createContext(false);
const useCanEdit = () => useContext(CanEdit);

/**
 * The rows a record can link to — suppliers, assets, risks — read once for the pickers and for
 * naming a DPIA's linked risks. The API checks every id is this organisation's (ownedIds).
 */
interface LinkOption { id: string; label: string }
interface LinkOptionsData { suppliers: LinkOption[]; assets: LinkOption[]; risks: LinkOption[]; ropa: LinkOption[] }
const LinkOptions = createContext<LinkOptionsData>({ suppliers: [], assets: [], risks: [], ropa: [] });
const useLinkOptions = () => useContext(LinkOptions);

/** Picks rows of another register; says so when that register is empty. */
function LinkPicker({ options, selected, onChange, empty }: {
  options: LinkOption[]; selected: string[]; onChange: (ids: string[]) => void; empty: string;
}) {
  // A link to a row the list no longer offers is kept and shown, never silently dropped.
  const shown = [...options, ...selected.filter((id) => !options.some((o) => o.id === id)).map((id) => ({ id, label: "Linked record" }))];
  if (shown.length === 0) return <div style={{ fontSize: 12, color: theme.color.faint }}>{empty}</div>;
  return (
    <div style={{ maxHeight: 150, overflowY: "auto" }}>
      <MultiSelect options={shown} selected={selected} onChange={onChange} />
    </div>
  );
}

const linkNames = (rows: LinkedRow[] | undefined) => (rows && rows.length ? rows.map((r) => r.name).join(", ") : null);

/** "legal_obligation" → "Legal obligation". */
const humanise = (v: string) => { const s = v.replace(/_/g, " "); return s.charAt(0).toUpperCase() + s.slice(1); };
const humaniseList = (vs: string[]) => (vs.length ? vs.map(humanise).join(", ") : null);

/** A small action button inside RecordActions. */
const actionBtn: React.CSSProperties = { ...btnSecondary, padding: "6px 12px", fontSize: text.hint.fontSize };
const actionDangerBtn: React.CSSProperties = { ...actionBtn, color: "var(--c-dangerText)", borderColor: "var(--c-danger)" };

/** The expanded, read-only body of a register card. Clicks inside it never collapse the card. */
function CardView({ children }: { children: React.ReactNode }) {
  return <div style={{ marginTop: 14 }} onClick={(e) => e.stopPropagation()}>{children}</div>;
}

/** Header row of the expanded view: tags on the left, Edit on the right. */
function ViewHead({ tags, onEdit }: { tags: React.ReactNode; onEdit?: () => void }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
      <div style={{ flex: 1, minWidth: 0 }}>{tags}</div>
      {onEdit && <EditButton onClick={onEdit} />}
    </div>
  );
}

/** datetime-local value for an ISO timestamp (local time). */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function dsrDaysLeft(dueAt: string): number {
  return Math.ceil((new Date(dueAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function deadlineBadge(dueAt: string, status: string) {
  if (status === "completed" || status === "refused" || status === "withdrawn") return null;
  const days = dsrDaysLeft(dueAt);
  let bg: string, text: string, label: string;
  if (days < 0) {
    bg = "var(--c-dangerBg)"; text = "var(--c-dangerText)";
    label = `Overdue by ${Math.abs(days)}d`;
  } else if (days <= 5) {
    bg = "var(--c-warningBg)"; text = "var(--c-warningText)";
    label = `${days}d left`;
  } else {
    bg = "var(--c-successBg)"; text = "var(--c-successText)";
    label = `${days}d left`;
  }
  return (
    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 600, background: bg, color: text, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

function MultiSelect({
  options,
  selected,
  onChange,
}: {
  options: { id: string; label: string }[];
  selected: string[];
  onChange: (updated: string[]) => void;
}) {
  function toggle(id: string) {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {options.map((opt) => {
        const on = selected.includes(opt.id);
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => toggle(opt.id)}
            style={{
              padding: "4px 12px", borderRadius: 3, fontSize: 12,
              border: on ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)",
              background: on ? "var(--c-accentBg)" : "var(--c-panel)",
              color: on ? "var(--c-accentText)" : theme.color.muted,
              fontWeight: on ? 600 : 400, cursor: "pointer",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose?: () => void }) {
  // Escape closes the dialog, as every other dialog does.
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ ...floatingSheet, width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/** A refusal shown inside the open dialog, where the person is looking (never behind it). */
function ModalError({ message }: { message: string }) {
  return (
    <div role="alert" style={{ gridColumn: "1 / -1", padding: "8px 12px", borderRadius: 3, background: "var(--c-dangerBg)", color: "var(--c-dangerText)", fontSize: 13 }}>
      {message}
    </div>
  );
}

function ModalHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--c-line)" }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>{title}</div>
      <button onClick={onClose} style={{ background: "none", border: "none", color: theme.color.muted, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
    </div>
  );
}

function FormField({ label, children, span2 }: { label: string; children: React.ReactNode; span2?: boolean }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13, gridColumn: span2 ? "1 / -1" : undefined }}>
      <span style={{ fontWeight: 500, color: theme.color.text }}>{label}</span>
      {children}
    </label>
  );
}

// ── ROPA Tab ───────────────────────────────────────────────────────────────────

interface RopaFormData {
  name: string;
  purpose: string;
  controllerProcessor: string;
  lawfulBases: string[];
  dataCategories: string[];
  subjectCategories: string[];
  retentionPeriod: string;
  recipients: string;
  thirdCountryTransfer: boolean;
  transferSafeguards: string;
  notes: string;
  processorIds: string[];
  systemIds: string[];
  retentionValue: string;
  retentionUnit: string;
  retentionTrigger: string;
  retentionReviewAt: string;
  transferCountries: string;
  largeScale: boolean;
  dpiaCriteria: string[];
  dpiaMandatoryCases: string[];
  dpiaScreened: boolean;
  dpiaNotRequiredReason: string;
  monitorsWorkers: boolean;
  /** Law review package F particulars (components/DpRegisterFields.tsx). */
  particulars: RopaParticularsForm;
}

/** Adds a processing activity, or (given `initial`) edits one. */
/** "EU" / "EEA" declare every member state (backend lib/dataProtectionLinks.ts isDeclaredLocation). */
const TRANSFER_PLACES = ["EU", "EEA", ...COUNTRIES];

/** The destinations typed, trimmed (stray commas dropped), and any that are not a country the platform knows. */
function parseTransferCountries(raw: string): { places: string[]; unknown: string[] } {
  const known = new Map(TRANSFER_PLACES.map((c) => [c.toLowerCase(), c]));
  const places: string[] = [];
  const unknown: string[] = [];
  for (const part of raw.split(",").map((x) => x.trim()).filter(Boolean)) {
    const match = known.get(part.toLowerCase());
    if (match) { if (!places.includes(match)) places.push(match); } else unknown.push(part);
  }
  return { places, unknown };
}

function RopaAddModal({ onSave, onClose, saving, initial, error, onRegistersChanged }: {
  onSave: (data: RopaFormData) => Promise<void>;
  onClose: () => void;
  saving: boolean;
  initial?: RopaEntry;
  error?: string | null;
  /** A processing agreement was added or its terms recorded from the form: the list, linkage and attention are read again. */
  onRegistersChanged?: () => void;
}) {
  const [localError, setLocalError] = useState<string | null>(null);
  const links = useLinkOptions();
  const dpCtx = useDp();
  const [form, setForm] = useState<RopaFormData>(initial ? {
    name: initial.name, purpose: initial.purpose, controllerProcessor: initial.controllerProcessor,
    lawfulBases: initial.lawfulBases, dataCategories: initial.dataCategories, subjectCategories: initial.subjectCategories,
    retentionPeriod: initial.retentionPeriod ?? "", recipients: initial.recipients.join(", "),
    thirdCountryTransfer: initial.thirdCountryTransfer, transferSafeguards: initial.transferSafeguards ?? "", notes: initial.notes ?? "",
    processorIds: (initial.processors ?? []).map((p) => p.id), systemIds: (initial.systems ?? []).map((s) => s.id),
    retentionValue: initial.retentionValue == null ? "" : String(initial.retentionValue), retentionUnit: initial.retentionUnit ?? "years",
    retentionTrigger: initial.retentionTrigger ?? "", retentionReviewAt: toDateInput(initial.retentionReviewAt ?? null),
    transferCountries: (initial.transferCountries ?? []).join(", "), largeScale: !!initial.largeScale,
    dpiaCriteria: initial.dpiaCriteria ?? [], dpiaMandatoryCases: initial.dpiaMandatoryCases ?? [],
    dpiaScreened: !!initial.dpiaScreenedAt, dpiaNotRequiredReason: initial.dpiaNotRequiredReason ?? "",
    monitorsWorkers: !!initial.monitorsWorkers,
    particulars: ropaParticularsFrom(initial as unknown as Record<string, unknown>),
  } : {
    name: "", purpose: "", controllerProcessor: "controller",
    lawfulBases: [], dataCategories: [], subjectCategories: [],
    retentionPeriod: "", recipients: "", thirdCountryTransfer: false,
    transferSafeguards: "", notes: "", processorIds: [], systemIds: [],
    retentionValue: "", retentionUnit: "years", retentionTrigger: "", retentionReviewAt: "", transferCountries: "", largeScale: false,
    dpiaCriteria: [], dpiaMandatoryCases: [], dpiaScreened: false, dpiaNotRequiredReason: "", monitorsWorkers: false,
    particulars: ropaParticularsFrom(null),
  });
  const dp = dpCtx;
  const { rows: agreements, reload: reloadAgreements } = useReloadableAgreementOptions();
  const canEditRegisters = useCanEdit();
  const setParticulars = (p: Partial<RopaParticularsForm>) => setForm((f) => ({ ...f, particulars: { ...f.particulars, ...p } }));
  // Recognised legitimate interests is a UK basis (UK GDPR Art. 6(1)(ea)): offered where UK law applies, or kept if already chosen.
  const lawfulBasisOptions = LAWFUL_BASES.filter((b) => !b.ukOnly || dp.ukLaw || form.lawfulBases.includes(b.id));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Destinations are read against Operating Locations and adequacy, so each must be a real country.
    if (form.thirdCountryTransfer) {
      const { places, unknown } = parseTransferCountries(form.transferCountries);
      if (unknown.length) {
        setLocalError(`Not a country the platform knows: ${unknown.join(", ")}. Choose from the list (or EU / EEA).`);
        return;
      }
      setLocalError(null);
      await onSave({ ...form, transferCountries: places.join(", ") });
      return;
    }
    setLocalError(null);
    await onSave(form);
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={initial ? "Edit processing activity" : "Add Processing Activity"} onClose={onClose} />
      <form onSubmit={handleSubmit} style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <FormField label="Name *" span2>
          <input required maxLength={200} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Employee payroll processing" style={{ ...inputStyle, fontSize: 13 }} />
        </FormField>
        <FormField label="Purpose *" span2>
          <textarea required value={form.purpose} onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))}
            rows={2} placeholder="What is the purpose of this processing activity?"
            style={{ ...inputStyle, fontSize: 13, resize: "vertical" }} />
        </FormField>
        <FormField label="Role" span2>
          <select value={form.controllerProcessor} onChange={(e) => setForm((f) => ({ ...f, controllerProcessor: e.target.value }))}
            style={{ ...inputStyle, fontSize: 13 }}>
            <option value="controller">Controller</option>
            <option value="processor">Processor</option>
            <option value="joint_controller">Joint Controller</option>
          </select>
        </FormField>
        <FormField label="Lawful Bases" span2>
          <MultiSelect options={lawfulBasisOptions} selected={form.lawfulBases} onChange={(v) => setForm((f) => ({ ...f, lawfulBases: v }))} />
        </FormField>
        <FormField label="Data Categories" span2>
          <DataCategoryPicker selected={form.dataCategories} onChange={(v) => setForm((f) => ({ ...f, dataCategories: v }))} />
        </FormField>
        <FormField label="Subject Categories" span2>
          <MultiSelect options={SUBJECT_CATEGORIES} selected={form.subjectCategories} onChange={(v) => setForm((f) => ({ ...f, subjectCategories: v }))} />
        </FormField>
        <FormField label="Large-scale processing?" span2>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 400 }}>
            <input type="checkbox" checked={form.largeScale} onChange={(e) => setForm((f) => ({ ...f, largeScale: e.target.checked }))} />
            The activity covers a large number of people, a large volume of data or a wide area (with special category data, a DPIA is mandatory)
          </label>
        </FormField>
        <FormField label="DPIA screening (GDPR Art. 35, WP248)" span2>
          <RopaDpiaScreeningFields
            record={{ dataCategories: form.dataCategories, subjectCategories: form.subjectCategories, largeScale: form.largeScale, declaredUkTypes: form.particulars.ukHighRiskTypes }}
            aiRiskClasses={(initial?.aiSystems ?? []).map((a) => a.riskClass)}
            ukLaw={dpCtx.ukLaw}
            value={{ dpiaCriteria: form.dpiaCriteria, dpiaMandatoryCases: form.dpiaMandatoryCases, dpiaScreened: form.dpiaScreened, dpiaNotRequiredReason: form.dpiaNotRequiredReason, monitorsWorkers: form.monitorsWorkers }}
            onChange={(v) => setForm((f) => ({ ...f, ...v }))}
          />
        </FormField>
        <FormField label="Retention period">
          <div style={{ display: "flex", gap: 6 }}>
            <input type="number" min={1} max={1000} value={form.retentionValue} onChange={(e) => setForm((f) => ({ ...f, retentionValue: e.target.value }))}
              placeholder="e.g. 6" style={{ ...inputStyle, fontSize: 13, width: 80 }} aria-label="Retention period length" />
            <select value={form.retentionUnit} onChange={(e) => setForm((f) => ({ ...f, retentionUnit: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }} aria-label="Retention period unit">
              {RETENTION_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </FormField>
        <FormField label="Counted">
          <select value={form.retentionTrigger} onChange={(e) => setForm((f) => ({ ...f, retentionTrigger: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }}>
            <option value="">— Not stated —</option>
            {RETENTION_TRIGGERS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </FormField>
        <FormField label="Retention notes">
          <input value={form.retentionPeriod} onChange={(e) => setForm((f) => ({ ...f, retentionPeriod: e.target.value }))}
            placeholder="e.g. 7 years after the tax year ends" style={{ ...inputStyle, fontSize: 13 }} />
        </FormField>
        <FormField label="Next review of the period">
          <input type="date" value={form.retentionReviewAt} onChange={(e) => setForm((f) => ({ ...f, retentionReviewAt: e.target.value }))}
            style={{ ...inputStyle, fontSize: 13 }} />
        </FormField>
        <FormField label="Recipients (comma-separated)">
          <input value={form.recipients} onChange={(e) => setForm((f) => ({ ...f, recipients: e.target.value }))}
            placeholder="e.g. HMRC, Payroll provider" style={{ ...inputStyle, fontSize: 13 }} />
        </FormField>
        <FormField label="Processors (suppliers that process this data)" span2>
          <LinkPicker options={links.suppliers} selected={form.processorIds} onChange={(v) => setForm((f) => ({ ...f, processorIds: v }))}
            empty="No suppliers recorded yet. Add them under Suppliers to link them here." />
        </FormField>
        {/* Outside the label above: a click on a standing or a button must not toggle the first processor checkbox. */}
        {form.processorIds.length > 0 && (
          <div style={{ gridColumn: "1 / -1" }}>
            <RopaProcessorAgreements supplierIds={form.processorIds} onChanged={onRegistersChanged} />
          </div>
        )}
        <FormField label="Systems (assets that hold or move this data)" span2>
          <LinkPicker options={links.assets} selected={form.systemIds} onChange={(v) => setForm((f) => ({ ...f, systemIds: v }))}
            empty="No assets recorded yet. Add them to the asset register to link them here." />
        </FormField>
        <FormField label="Third Country Transfer?" span2>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 400 }}>
            <input type="checkbox" checked={form.thirdCountryTransfer}
              onChange={(e) => setForm((f) => ({ ...f, thirdCountryTransfer: e.target.checked }))} />
            Data is transferred outside the UK/EEA
          </label>
        </FormField>
        {form.thirdCountryTransfer && (
          <>
            <FormField label="Transfer destinations (comma-separated)" span2>
              <input value={form.transferCountries} onChange={(e) => setForm((f) => ({ ...f, transferCountries: e.target.value }))}
                list="ropa-transfer-places" placeholder="e.g. United States, India" style={{ ...inputStyle, fontSize: 13 }} />
              <datalist id="ropa-transfer-places">
                {TRANSFER_PLACES.map((c) => <option key={c} value={c} />)}
              </datalist>
            </FormField>
            <FormField label="Transfer Safeguards" span2>
              <textarea value={form.transferSafeguards} onChange={(e) => setForm((f) => ({ ...f, transferSafeguards: e.target.value }))}
                rows={2} placeholder="e.g. Standard Contractual Clauses, Adequacy decision"
                style={{ ...inputStyle, fontSize: 13, resize: "vertical" }} />
            </FormField>
          </>
        )}
        <RopaParticularsFields
          value={form.particulars} onChange={setParticulars} role={form.controllerProcessor} lawfulBases={form.lawfulBases}
          dataCategories={form.dataCategories} thirdCountryTransfer={form.thirdCountryTransfer} ukInScope={!!dp.ukLaw} agreements={agreements}
          recipients={form.recipients.split(",").map((x) => x.trim()).filter(Boolean)}
          canAddAgreement={canEditRegisters} onAgreementAdded={() => { reloadAgreements(); onRegistersChanged?.(); }}
        />
        <FormField label="Notes" span2>
          <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={2} placeholder="Any additional context…" style={{ ...inputStyle, fontSize: 13, resize: "vertical" }} />
        </FormField>
        {(localError || error) && <ModalError message={(localError || error)!} />}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
          <button type="submit" disabled={saving}
            style={{ ...btnPrimary, opacity: saving ? 0.6 : 1, cursor: saving ? "not-allowed" : "pointer" }}>
            {saving ? "Saving…" : initial ? "Save changes" : "Add Activity"}
          </button>
          <button type="button" onClick={onClose}
            style={{ padding: "9px 16px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "transparent", color: theme.color.muted, cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

/** Each processor named on an activity, with its agreement's Art. 28 standing and what it allows. */
function RopaProcessors({ entry }: { entry: RopaEntry }) {
  const navigate = usePortalNavigate();
  const rows = entry.linkage?.processors ?? [];
  if (!rows.length) return null;
  return (
    <DetailSection title="Processors and their agreements">
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((p) => (
          <div key={p.supplierId} style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", fontSize: 13 }}>
            <button type="button" onClick={() => navigate(`/tprm?supplier=${p.supplierId}`)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: theme.color.text, fontWeight: 500, fontSize: 13 }}>
              {p.name}
            </button>
            <Pill label={DPA_STANDING[p.standing].label} colors={DPA_STANDING[p.standing].colors} />
            {!p.isDataProcessor && <span style={{ ...text.hint, color: "var(--c-warningText)" }}>not marked as a data processor</span>}
            {p.standing !== "no_agreement" && p.returnOrDeletion !== "yes" && <span style={{ ...text.hint }}>return or deletion {p.returnOrDeletion === "no" ? "not in the agreement" : "not checked"}</span>}
            {p.dataLocations.length > 0 && <span style={{ ...text.hint }}>may hold data in {p.dataLocations.join(", ")}</span>}
          </div>
        ))}
      </div>
    </DetailSection>
  );
}

/** Where data discovery found this activity's data, and anything held past its retention period. */
function RopaDiscovered({ entry }: { entry: RopaEntry }) {
  const navigate = usePortalNavigate();
  const rows = entry.discoveredLocations ?? [];
  if (!rows.length) return null;
  const past = new Set((entry.linkage?.retention.heldPast ?? []).map((h) => h.findingId));
  const mayBePast = new Set((entry.linkage?.retention.mayHoldPast ?? []).map((h) => h.findingId));
  return (
    <DetailSection title="Where data discovery found it">
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((d) => (
          <div key={d.id} style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", fontSize: 13 }}>
            <button type="button" onClick={() => navigate(`/data-discovery?finding=${d.id}`)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: theme.color.text, fontSize: 13, textAlign: "left" }}>
              {d.location}
            </button>
            <span style={{ ...text.hint }}>{humanise(d.locationKind)} · {humanise(d.status)} · seen since {fmtDate(d.firstSeenAt)}{d.asset ? ` · ${d.asset.name}` : ""}</span>
            {past.has(d.id) && <Pill label="Unchanged for longer than the retention period" colors={{ bg: "var(--c-dangerBg)", text: "var(--c-dangerText)" }} />}
            {mayBePast.has(d.id) && <Pill label="Check items past retention are removed" colors={{ bg: "var(--c-warningBg)", text: "var(--c-warningText)" }} />}
          </div>
        ))}
      </div>
    </DetailSection>
  );
}

function RopaTab({ ropaEntries, attention, loading, reload, initialOpen }: { ropaEntries: RopaEntry[]; attention: DpAttentionItem[]; loading: boolean; reload: () => void; initialOpen?: string | null }) {
  const navigate = usePortalNavigate();
  const dp = useDp();
  const canEdit = useCanEdit();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<RopaEntry | null>(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(initialOpen ?? null);
  useDeepLinkScroll(initialOpen, !loading);
  const [error, setError] = useState<string | null>(null);

  async function handleAdd(data: RopaFormData) {
    setSaving(true);
    setError(null);
    try {
      const value = parseInt(data.retentionValue, 10);
      const { particulars, ...fields } = data;
      const body = {
        ...fields,
        ...ropaParticularsPayload(particulars),
        recipients: data.recipients ? data.recipients.split(",").map((s) => s.trim()).filter(Boolean) : [],
        retentionPeriod: data.retentionPeriod || null,
        transferSafeguards: data.transferSafeguards || null,
        notes: data.notes || null,
        retentionValue: Number.isFinite(value) && value > 0 ? value : null,
        retentionUnit: Number.isFinite(value) && value > 0 ? data.retentionUnit : null,
        retentionTrigger: data.retentionTrigger || null,
        retentionReviewAt: data.retentionReviewAt || null,
        // Destinations are only meaningful with a declared transfer.
        transferCountries: data.thirdCountryTransfer ? data.transferCountries.split(",").map((s) => s.trim()).filter(Boolean) : [],
        dpiaNotRequiredReason: data.dpiaNotRequiredReason.trim() || null,
      };
      // Saving an edit returns to the entry's view, which stays expanded.
      if (editing) await api.patch(`/gdpr/ropa/${editing.id}`, body);
      else await api.post("/gdpr/ropa", body);
      setShowAdd(false);
      setEditing(null);
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Remove "${name}" from your ROPA?`)) return;
    try {
      await api.del(`/gdpr/ropa/${id}`);
      reload();
    } catch { setError("Failed to delete entry."); }
  }

  return (
    <div>
      {(showAdd || editing) && <RopaAddModal key={editing?.id ?? "new"} initial={editing ?? undefined} onSave={handleAdd} onClose={() => { setShowAdd(false); setEditing(null); setError(null); }} saving={saving} error={error} onRegistersChanged={reload} />}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: theme.color.subtle }}>
          {ropaEntries.length} activit{ropaEntries.length === 1 ? "y" : "ies"} recorded
        </div>
        {canEdit && <button onClick={() => setShowAdd(true)} style={{ ...btnPrimary, cursor: "pointer" }}>+ Add Processing Activity</button>}
      </div>

      {error && (
        <div style={{ ...card, marginBottom: 12, padding: "10px 14px", background: "var(--c-dangerBg)", color: "var(--c-dangerText)", fontSize: 13 }}>{error}</div>
      )}

      <RopaDutiesBanner duties={dp.ropa.duties} />
      <DpIdentityPanel canEdit={canEdit} startOpen={new URLSearchParams(window.location.search).get("identity") === "1"} />

      {/* The record read against its processors, contracts, DPIAs and data discovery. */}
      {!loading && attention.length > 0 && (
        <div style={{ ...card, marginBottom: 16, padding: "12px 16px" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>
            {attention.length} finding{attention.length === 1 ? "" : "s"} across the record and the registers linked to it
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {attention.slice(0, 12).map((a) => (
              <div key={a.id} style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                <Pill label={a.severity} colors={SEVERITY_COLORS[a.severity] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <button type="button" onClick={() => navigate(a.href)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", fontSize: 13, color: theme.color.text, fontWeight: 500 }}>
                    {a.title}
                  </button>
                  <div style={{ ...text.hint }}>{a.description}</div>
                </div>
              </div>
            ))}
            {attention.length > 12 && <div style={{ ...text.hint }}>…and {attention.length - 12} more.</div>}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ ...card, fontSize: 13, color: theme.color.muted }}>Loading…</div>
      ) : ropaEntries.length === 0 ? (
        <div style={{ ...emptyFrame, padding: 40 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 6 }}>No processing activities yet</div>
          <div style={{ fontSize: 13, color: theme.color.muted }}>
            Your Record of Processing Activities (ROPA) documents how your organisation handles personal data — required under {dp.ropa.duties.length > 0 ? "the laws listed above" : "the data protection laws that apply to you"}.
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {ropaEntries.map((entry) => (
            <div key={entry.id} data-deeplink={entry.id} style={card}>
              <div
                style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, cursor: "pointer" }}
                onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading, marginBottom: 4 }}>{entry.name}</div>
                  <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 6, lineHeight: 1.5 }}>
                    {entry.purpose.length > 120 ? entry.purpose.slice(0, 120) + "…" : entry.purpose}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <Pill label={entry.controllerProcessor} colors={{ bg: "var(--c-accentBg)", text: "var(--c-accentText)" }} />
                    {entry.dataCategories.length > 0 && (
                      <span style={{ fontSize: 11, color: theme.color.muted }}>
                        {entry.dataCategories.length} data categor{entry.dataCategories.length === 1 ? "y" : "ies"}
                      </span>
                    )}
                    {entry.dpiaStatus && (
                      <Pill label={`DPIA: ${entry.dpiaStatus}`} colors={STATUS_COLORS[entry.dpiaStatus] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                    )}
                    {entry.thirdCountryTransfer && (
                      <span style={{ fontSize: 11, color: "var(--c-warningText)", fontWeight: 600 }}>Third country transfer</span>
                    )}
                    {entry.linkage && (entry.linkage.dpia.level === "mandatory" || (entry.linkage.dpia.level === "likely" && !entry.linkage.dpia.notRequired)) && !entry.linkage.dpia.covered && (
                      <Pill label={entry.linkage.dpia.level === "mandatory" ? "DPIA mandatory" : "DPIA likely needed"} colors={{ bg: "var(--c-dangerBg)", text: "var(--c-dangerText)" }} />
                    )}
                    {(entry.linkage?.processors ?? []).map((p) => (
                      <Pill key={p.supplierId} label={`${p.name}: ${DPA_STANDING[p.standing].label.toLowerCase()}`} colors={DPA_STANDING[p.standing].colors} />
                    ))}
                    {(entry.discoveredLocations?.length ?? 0) > 0 && (
                      <span style={{ fontSize: 11, color: theme.color.muted }}>
                        Found in {entry.discoveredLocations!.length} location{entry.discoveredLocations!.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                </div>
                <span style={{ color: theme.color.muted, fontSize: 16, marginTop: 2 }}>{expanded === entry.id ? "▲" : "▼"}</span>
              </div>

              {expanded === entry.id && (
                <CardView>
                  <ViewHead tags={<EntityTagsRow entity={{ kind: "ropa", id: entry.id }} readOnly={!canEdit} />} onEdit={canEdit ? () => setEditing(entry) : undefined} />
                  <NextSteps items={ropaSteps(entry)} />
                  <DetailSection title="Processing">
                    <DetailFields fields={[
                      { label: "Purpose", value: entry.purpose, wide: true, multiline: true },
                      { label: "Role", value: humanise(entry.controllerProcessor) },
                      { label: "Retention", value: [entry.linkage?.retention.label, entry.retentionPeriod].filter(Boolean).join(" — ") || null },
                      { label: "Retention review", value: entry.retentionReviewAt ? `${fmtDateOnly(entry.retentionReviewAt)}${entry.linkage?.retention.reviewOverdue ? " (overdue)" : ""}` : null },
                      { label: "Large-scale", value: entry.largeScale ? "Yes" : "No" },
                      { label: "Lawful bases", value: humaniseList(entry.lawfulBases), wide: true },
                      { label: "Data categories", value: entry.dataCategories.length ? entry.dataCategories.map(dataCategoryLabel).join(", ") : null },
                      { label: "Subject categories", value: humaniseList(entry.subjectCategories) },
                      { label: "Recipients", value: entry.recipients.length ? entry.recipients.join(", ") : null, wide: true },
                      { label: "Systems", value: linkNames(entry.systems), wide: true },
                      { label: "AI systems", value: entry.aiSystems?.length ? entry.aiSystems.map((a) => `${a.name} (${humanise(a.riskClass)} risk)`).join(", ") : null, wide: true },
                    ]} />
                  </DetailSection>
                  {entry.linkage && (
                    <DetailSection title="DPIA screening">
                      <DpiaScreeningSummary screening={entry.linkage.dpia} />
                      {entry.dpiaNotRequiredReason && entry.linkage.dpia.notRequired && (
                        <div style={{ fontSize: 13, color: theme.color.text, marginTop: 8, whiteSpace: "pre-wrap" }}>Why no DPIA: {entry.dpiaNotRequiredReason}</div>
                      )}
                    </DetailSection>
                  )}
                  <DetailSection title="Security, conditions and agreements">
                    <DetailFields fields={ropaParticularsDetail(
                      { ...ropaParticularsFrom(entry as unknown as Record<string, unknown>), controllerProcessor: entry.controllerProcessor, agreements: entry.agreements },
                      (ids, list) => ids.map((id) => list.find((x) => x.id === id)?.label ?? id).join("; "),
                    )} />
                  </DetailSection>
                  <RopaProcessors entry={entry} />
                  <RopaDiscovered entry={entry} />
                  <DetailSection title="Transfers and assessment">
                    <DetailFields fields={[
                      { label: "Third country transfer", value: entry.thirdCountryTransfer ? "Yes, outside the UK / EEA" : "No" },
                      ...(entry.thirdCountryTransfer ? [{ label: "Destinations", value: entry.transferCountries?.length ? entry.transferCountries.join(", ") : "Not recorded" }] : []),
                      ...(entry.linkage?.transfers.undeclared.length ? [{ label: "Allowed by processor agreements, not declared", value: entry.linkage.transfers.undeclared.map((u) => `${u.location} (${u.supplier})`).join(", "), wide: true }] : []),
                      { label: "Impact assessment", value: entry.dpiaStatus ? humanise(entry.dpiaStatus) : entry.linkage && entry.linkage.dpia.level !== "none" ? `None linked — ${entry.linkage.dpia.level === "mandatory" ? "mandatory" : entry.linkage.dpia.level === "likely" ? "likely needed" : "one criterion met"}${entry.linkage.dpia.notRequired ? " (recorded as not needed)" : ""}` : null, wide: true },
                      ...(entry.thirdCountryTransfer ? [{ label: "Transfer safeguards", value: entry.transferSafeguards, wide: true, multiline: true }] : []),
                      { label: "Notes", value: entry.notes, wide: true, multiline: true },
                    ]} />
                  </DetailSection>
                  {canEdit && (
                    <RecordActions>
                      <button type="button" onClick={() => handleDelete(entry.id, entry.name)} style={actionDangerBtn}>Delete activity</button>
                    </RecordActions>
                  )}
                </CardView>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── DSR Tab ────────────────────────────────────────────────────────────────────

interface DsrFormData {
  type: string;
  requestorName: string;
  requestorEmail: string;
  channel: string;
  receivedAt: string;
  notes: string;
  // Edit only: the request's own clock and outcome.
  dueAt: string;
  status: string;
  refusalReason: string;
  // The processing activities and systems the request reaches.
  ropaEntryIds: string[];
  systemIds: string[];
  /** UK Art. 12A dates and Art. 19 (components/DpRegisterFields.tsx). */
  law: DsrLawForm;
}

const DSR_STATUSES = ["open", "in_progress", "completed", "refused", "withdrawn"];
/** "YYYY-MM-DD" for a date input, from an ISO timestamp. */
// Received and due are calendar dates (lib/dateOnly.ts): box, label and saved value use the UTC date.
const dateInput = toDateInput;

/** Logs a request, or (given `initial`) edits one — including why it was refused. */
function DsrAddModal({ onSave, onClose, saving, initial, presetStatus, error }: {
  onSave: (data: DsrFormData) => Promise<void>;
  onClose: () => void;
  saving: boolean;
  initial?: DsrEntry;
  presetStatus?: string;
  error?: string | null;
}) {
  const today = todayInput();
  const [form, setForm] = useState<DsrFormData>(initial ? {
    type: initial.type, requestorName: initial.requestorName, requestorEmail: initial.requestorEmail ?? "", channel: initial.channel,
    receivedAt: dateInput(initial.receivedAt), notes: initial.notes ?? "",
    dueAt: dateInput(initial.dueAt), status: presetStatus ?? initial.status, refusalReason: initial.refusalReason ?? "",
    ropaEntryIds: initial.ropaEntryIds ?? [], systemIds: initial.systemIds ?? [],
    law: dsrLawFrom(initial as unknown as Record<string, unknown>),
  } : {
    type: "access", requestorName: "", requestorEmail: "", channel: "email",
    receivedAt: today, notes: "", dueAt: "", status: "open", refusalReason: "", ropaEntryIds: [], systemIds: [],
    law: dsrLawFrom({}),
  });
  const links = useLinkOptions();

  const { clocks, requestTypes } = useDp();
  // The kinds of request the organisation's laws provide (backend lib/rightsRequests.ts), keeping a stored legacy type.
  const typeOptions = [...new Set([...(requestTypes?.map((t) => t.id) ?? DSR_TYPES), ...(initial ? [initial.type] : [])])];
  // The latest due date the law allows from this received date (the server computes and enforces it).
  const statutoryDue = form.receivedAt
    ? statutoryRightsDeadline(new Date(`${form.receivedAt}T00:00:00Z`), clocks, !!initial?.extended)
    : null;
  const dueDatePreview = statutoryDue ? fmtDateOnly(statutoryDue) : "—";
  const latestDue = statutoryDue ? statutoryDue.toISOString().slice(0, 10) : undefined;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await onSave(form);
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={initial ? "Edit data subject request" : "New Data Subject Request"} onClose={onClose} />
      <form onSubmit={handleSubmit} style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <FormField label="Request Type *">
          <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }}>
            {typeOptions.map((t) => <option key={t} value={t}>{REQUEST_TYPE_LABELS[t] ?? t.replace(/_/g, " ")}</option>)}
          </select>
        </FormField>
        <FormField label="Channel">
          <select value={form.channel} onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }}>
            {DSR_CHANNELS.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
        </FormField>
        <FormField label="Requestor Name *">
          <input required value={form.requestorName} onChange={(e) => setForm((f) => ({ ...f, requestorName: e.target.value }))}
            placeholder="Jane Smith" style={{ ...inputStyle, fontSize: 13 }} />
        </FormField>
        <FormField label="Requestor Email">
          <input type="email" value={form.requestorEmail} onChange={(e) => setForm((f) => ({ ...f, requestorEmail: e.target.value }))}
            placeholder="jane@example.com" style={{ ...inputStyle, fontSize: 13 }} />
        </FormField>
        <FormField label="Received Date *">
          <input type="date" required value={form.receivedAt} max={today}
            onChange={(e) => setForm((f) => ({
              ...f, receivedAt: e.target.value,
              // The clock runs from receipt: a corrected received date moves the deadline with it.
              ...(initial && e.target.value
                ? { dueAt: statutoryRightsDeadline(new Date(`${e.target.value}T00:00:00Z`), clocks, initial.extended).toISOString().slice(0, 10) }
                : {}),
            }))}
            style={{ ...inputStyle, fontSize: 13 }} />
        </FormField>
        {initial ? (
          <FormField label={`Due date (by ${dueDatePreview} at the latest)`}>
            <input type="date" required value={form.dueAt} min={form.receivedAt || undefined} max={latestDue}
              onChange={(e) => setForm((f) => ({ ...f, dueAt: e.target.value }))}
              style={{ ...inputStyle, fontSize: 13 }} />
          </FormField>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
            <span style={{ fontWeight: 500, color: theme.color.text }}>Due date (auto)</span>
            <div style={{ ...inputStyle, fontSize: 13, color: theme.color.muted, display: "flex", alignItems: "center" }}>
              {dueDatePreview} <span style={{ fontSize: 11, marginLeft: 8, color: theme.color.faint }}>({clockLabel(clocks.rights, "30 days")})</span>
            </div>
          </div>
        )}
        {initial && (
          <FormField label="Status">
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }}>
              {DSR_STATUSES.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
            </select>
          </FormField>
        )}
        {initial && form.status === "refused" && (
          <FormField label="Reason for refusing *" span2>
            <textarea required value={form.refusalReason} onChange={(e) => setForm((f) => ({ ...f, refusalReason: e.target.value }))}
              rows={2} placeholder="The exemption or ground relied on, as told to the requester (e.g. manifestly unfounded or excessive)"
              style={{ ...inputStyle, fontSize: 13, resize: "vertical" }} />
          </FormField>
        )}
        <FormField label="Processing activities it reaches" span2>
          <LinkPicker options={links.ropa} selected={form.ropaEntryIds} onChange={(v) => setForm((f) => ({ ...f, ropaEntryIds: v }))}
            empty="No processing activities recorded yet." />
        </FormField>
        <FormField label="Other systems to search" span2>
          <LinkPicker options={links.assets} selected={form.systemIds} onChange={(v) => setForm((f) => ({ ...f, systemIds: v }))}
            empty="No assets recorded yet." />
        </FormField>
        <DsrLawFields value={form.law} onChange={(p) => setForm((f) => ({ ...f, law: { ...f.law, ...p } }))} type={form.type} />
        <FormField label="Notes" span2>
          <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={2} placeholder="Any additional context about this request…" style={{ ...inputStyle, fontSize: 13, resize: "vertical" }} />
        </FormField>
        {error && <ModalError message={error} />}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
          <button type="submit" disabled={saving}
            style={{ ...btnPrimary, opacity: saving ? 0.6 : 1, cursor: saving ? "not-allowed" : "pointer" }}>
            {saving ? "Saving…" : initial ? "Save changes" : "Create Request"}
          </button>
          <button type="button" onClick={onClose}
            style={{ padding: "9px 16px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "transparent", color: theme.color.muted, cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

type DsrAction = "complete" | "in_progress" | "refuse" | "extend" | "pause" | "resume";
/** What an extend or pause action sends (the reason, and when the requester was told). */
type DsrActionBody = Record<string, string>;
const CLOSED_DSR = ["completed", "refused", "withdrawn"];

const EXTENSION_REASONS: Array<{ value: string; label: string }> = [
  { value: "complexity", label: "Complexity of the request" },
  { value: "number_of_requests", label: "Number of requests" },
];
const PAUSE_REASONS: Array<{ value: string; label: string }> = [
  { value: "identity", label: "Awaiting identity information" },
  { value: "clarification", label: "Awaiting clarification" },
  { value: "fee", label: "Awaiting a fee" },
];

/** The read-only view of one data subject request, with its status changes under Actions. */
function DsrView({ entry, busy, onAction, onEdit, onChanged }: { entry: DsrEntry; busy: boolean; onAction: (a: DsrAction, body?: DsrActionBody) => void; onEdit: () => void; onChanged: () => void }) {
  const { clocks, requestTypes } = useDp();
  const canEdit = useCanEdit();
  const isOpen = !CLOSED_DSR.includes(entry.status);
  const closedAt = entry.closedAt ?? entry.completedAt ?? null;
  const [panel, setPanel] = useState<null | "extend" | "pause">(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [toldOn, setToldOn] = useState(todayInput());
  // The clock this kind of request runs on: a US state law's own period is never extended or stopped here.
  const typeClock = requestTypes?.find((t) => t.id === entry.type)?.clock;
  const rightsClock = !typeClock || typeClock.kind === "rights" || typeClock.kind === "working_limit";
  const pauses = entry.clockPauses ?? [];
  const stopped = rightsClock && pauses.some((p) => p.to === null && (p.reason !== "clarification" || entry.type === "access"));
  const received = new Date(entry.receivedAt);
  // How much of each stop counts (identity and fee from receipt, clarification of an access request only).
  const paused = pausedMs(countedPauses(pauses, entry.type, received));
  // Each law keeps its own extension: the extended deadline is the earliest outer limit among them.
  const firstDeadline = statutoryRightsDeadline(received, clocks, { pausedMs: paused });
  const extendedDeadline = statutoryRightsDeadline(received, clocks, { extended: true, pausedMs: paused });
  const extendable = !typeClock || typeClock.kind === "rights";
  const canExtend = extendable && !entry.extended && extendedDeadline.getTime() > firstDeadline.getTime() && new Date(entry.dueAt).getTime() >= Date.now();
  const canPause = rightsClock && (clocks.rightsLaws ?? []).some((l) => l.pause);
  const pauseReasons = PAUSE_REASONS.filter((r) => r.value !== "clarification" || entry.type === "access");
  const extensionWords = EXTENSION_REASONS.find((r) => r.value === entry.extensionReason)?.label;
  return (
    <CardView>
      <ViewHead tags={null} onEdit={canEdit ? onEdit : undefined} />
      <NextSteps items={dsrSteps(entry)} />
      <DetailSection title="Request">
        <DetailFields fields={[
          { label: "Type", value: humanise(entry.type) },
          { label: "Status", value: humanise(entry.status) },
          { label: "Requester", value: entry.requestorName },
          { label: "Email", value: entry.requestorEmail },
          { label: "Channel", value: humanise(entry.channel) },
          ...(entry.status === "refused" ? [{ label: "Reason for refusing", value: entry.refusalReason ?? null, wide: true, multiline: true }] : []),
          { label: "Notes", value: entry.notes, wide: true, multiline: true },
        ]} />
      </DetailSection>
      {entry.actionList && (entry.actionList.activities.length > 0 || entry.actionList.systems.length > 0) && (
        <DetailSection title="To action">
          <DetailFields fields={[
            { label: "Processing activities", value: entry.actionList.activities.map((a) => a.name).join(", ") || null, wide: true },
            { label: "Systems to search or change", value: entry.actionList.systems.map((x) => `${x.name} (${x.via.join(", ")})`).join("; ") || null, wide: true },
            { label: "Processors to instruct", value: entry.actionList.processors.map((x) => `${x.name} (${x.via.join(", ")})`).join("; ") || null, wide: true },
          ]} />
        </DetailSection>
      )}
      <DsrDataChecklist requestId={entry.id} isOpen={isOpen} canEdit={canEdit} onChanged={onChanged} />
      <DetailSection title="Timeline" aside={deadlineBadge(entry.dueAt, entry.status)}>
        <DetailFields fields={[
          { label: "Received", value: fmtDateOnly(entry.receivedAt) },
          { label: "Due", value: `${fmtDateOnly(entry.dueAt)} (${typeClock && typeClock.kind !== "rights" && typeClock.label ? typeClock.label : clockLabel(clocks.rights, "30 days")}${entry.extended ? ", extended" : ""}${stopped ? ", clock stopped" : ""})` },
          ...(entry.extended ? [{ label: "Extension", value: `${extensionWords ?? "Reason not recorded"}${entry.extensionNote ? ` (${entry.extensionNote})` : ""}${entry.requesterInformedAt ? `; requester told ${fmtDateOnly(entry.requesterInformedAt)}` : ""}`, wide: true }] : []),
          ...(entry.recipientsNotified ? [{ label: "Recipients told (Art. 19)", value: `${humanise(entry.recipientsNotified)}${entry.recipientsNotifiedNote ? `: ${entry.recipientsNotifiedNote}` : ""}` }] : []),
          ...(pauses.length ? [{ label: "Clock stopped", value: pauses.map((p) => `${PAUSE_REASONS.find((r) => r.value === p.reason)?.label ?? p.reason}: ${fmtDateOnly(p.from)} to ${p.to ? fmtDateOnly(p.to) : "now"}`).join("; "), wide: true }] : []),
          { label: "Closed", value: closedAt ? fmtDate(closedAt) : null },
        ]} />
      </DetailSection>
      {canEdit && isOpen && panel === "extend" && (
        <DetailSection title="Extend the response period">
          <p style={{ ...text.hint, margin: "0 0 8px" }}>
            An extension is for the complexity or number of requests, and the requester must be told, with the reasons, by {fmtDateOnly(firstDeadline.toISOString())}. The new deadline is {fmtDateOnly(extendedDeadline.toISOString())}: no law in effect allows longer.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select value={reason} onChange={(e) => setReason(e.target.value)} style={inputStyle} aria-label="Reason for the extension">
              <option value="">Reason…</option>
              {EXTENSION_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <label style={text.hint}>Requester told on <input type="date" value={toldOn} max={todayInput()} onChange={(e) => setToldOn(e.target.value)} style={inputStyle} /></label>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} placeholder="Details (optional)" aria-label="Details of the extension" style={{ ...inputStyle, minWidth: 220 }} />
            <button type="button" disabled={busy || !reason || !toldOn} onClick={() => { onAction("extend", { reason, requesterInformedAt: dateInputToIso(toldOn) ?? "", ...(note.trim() ? { note: note.trim() } : {}) }); setPanel(null); }} style={actionBtn}>Record extension</button>
            <button type="button" onClick={() => setPanel(null)} style={actionBtn}>Cancel</button>
          </div>
        </DetailSection>
      )}
      {canEdit && isOpen && panel === "pause" && (
        <DetailSection title="Stop the clock">
          <p style={{ ...text.hint, margin: "0 0 8px" }}>
            The response period stops while identity information, a fee or clarification of an access request is awaited (UK GDPR Art. 12A): identity and fee count from receipt until they arrive, clarification from asking until it arrives. It moves only the deadline of a law that allows it; another law's earlier deadline still binds.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select value={reason} onChange={(e) => setReason(e.target.value)} style={inputStyle} aria-label="Why the clock stops">
              <option value="">Reason…</option>
              {pauseReasons.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <button type="button" disabled={busy || !reason} onClick={() => { onAction("pause", { reason }); setPanel(null); }} style={actionBtn}>Stop the clock</button>
            <button type="button" onClick={() => setPanel(null)} style={actionBtn}>Cancel</button>
          </div>
        </DetailSection>
      )}
      {canEdit && isOpen && (
        <RecordActions>
          {entry.status !== "completed" && (
            <button type="button" disabled={busy || (!!entry.checklist?.built && entry.checklist.open > 0)} onClick={() => onAction("complete")} style={actionBtn}
              title={entry.checklist?.built && entry.checklist.open > 0 ? "Deal with every place on the checklist first" : undefined}>Mark completed</button>
          )}
          {entry.status === "open" && <button type="button" disabled={busy} onClick={() => onAction("in_progress")} style={actionBtn}>Mark in progress</button>}
          {/* An extension is claimed within the original period: an overdue request cannot be extended. */}
          {canExtend && (
            <button type="button" disabled={busy} onClick={() => { setReason(""); setPanel("extend"); }} title={`Extend to ${fmtDateOnly(extendedDeadline.toISOString())}`} style={actionBtn}>
              Extend to {fmtDateOnly(extendedDeadline.toISOString())}
            </button>
          )}
          {canPause && !stopped && <button type="button" disabled={busy} onClick={() => { setReason(""); setPanel("pause"); }} style={actionBtn}>Stop the clock</button>}
          {stopped && <button type="button" disabled={busy} onClick={() => onAction("resume")} style={actionBtn}>Restart the clock</button>}
          {entry.status !== "refused" && <button type="button" disabled={busy} onClick={() => onAction("refuse")} style={actionDangerBtn}>Refuse</button>}
        </RecordActions>
      )}
    </CardView>
  );
}

function DsrTab({ dsrEntries, loading, reload, initialOpen }: { dsrEntries: DsrEntry[]; loading: boolean; reload: () => void; initialOpen?: string | null }) {
  const { clocks } = useDp();
  const canEdit = useCanEdit();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<{ entry: DsrEntry; status?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(initialOpen ?? null);
  useDeepLinkScroll(initialOpen, !loading);
  const [error, setError] = useState<string | null>(null);
  const [actioning, setActioning] = useState<string | null>(null);

  // The API takes ISO date-times with an offset; the date inputs give "YYYY-MM-DD", sent as UTC midnight.
  const isoDay = (d: string) => dateInputToIso(d);

  async function handleAdd(data: DsrFormData) {
    setSaving(true);
    setError(null);
    try {
      const common = {
        type: data.type, channel: data.channel, requestorName: data.requestorName,
        requestorEmail: data.requestorEmail || null,
        notes: data.notes || null,
        ropaEntryIds: data.ropaEntryIds,
        systemIds: data.systemIds,
        ...dsrLawPayload(data.law),
      };
      if (editing) {
        const was = editing.entry;
        await api.patch(`/gdpr/dsr/${was.id}`, {
          ...common,
          // A date left as it was keeps its stored time of day.
          ...(data.receivedAt && data.receivedAt !== dateInput(was.receivedAt) ? { receivedAt: isoDay(data.receivedAt) } : {}),
          ...(data.dueAt && data.dueAt !== dateInput(was.dueAt) ? { dueAt: isoDay(data.dueAt) } : {}),
          status: data.status,
          refusalReason: data.status === "refused" ? data.refusalReason.trim() || null : editing.entry.refusalReason ?? null,
        });
        setEditing(null);
      } else {
        await api.post("/gdpr/dsr", { ...common, receivedAt: isoDay(data.receivedAt) });
        setShowAdd(false);
      }
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleAction(id: string, action: DsrAction, body?: DsrActionBody) {
    // Refusing records why: the edit form opens with the status set, asking for the reason.
    if (action === "refuse") {
      const entry = dsrEntries.find((d) => d.id === id);
      if (entry) setEditing({ entry, status: "refused" });
      return;
    }
    // A rectification, erasure or restriction closes with whether its recipients were told (Art. 19).
    if (action === "complete") {
      const entry = dsrEntries.find((d) => d.id === id);
      if (entry && ["rectification", "erasure", "restriction"].includes(entry.type) && !entry.recipientsNotified) {
        setEditing({ entry, status: "completed" });
        return;
      }
    }
    setActioning(id);
    try {
      const statusMap: Record<string, string> = { complete: "completed", in_progress: "in_progress", refuse: "refused" };
      if (action === "extend") {
        await api.patch(`/gdpr/dsr/${id}/extend`, body ?? {});
      } else if (action === "pause" || action === "resume") {
        await api.post(`/gdpr/dsr/${id}/${action}`, body ?? {});
      } else {
        await api.patch(`/gdpr/dsr/${id}`, { status: statusMap[action] });
      }
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to update");
    } finally {
      setActioning(null);
    }
  }

  // Sort by urgency: overdue first, then by days left ascending
  const sorted = [...dsrEntries].sort((a, b) => {
    const da = new Date(a.dueAt).getTime();
    const db = new Date(b.dueAt).getTime();
    return da - db;
  });

  const open = sorted.filter((e) => !["completed", "refused", "withdrawn"].includes(e.status));
  const closed = sorted.filter((e) => ["completed", "refused", "withdrawn"].includes(e.status));

  return (
    <div>
      {showAdd && <DsrAddModal onSave={handleAdd} onClose={() => { setShowAdd(false); setError(null); }} saving={saving} error={error} />}
      {editing && <DsrAddModal key={editing.entry.id} initial={editing.entry} presetStatus={editing.status} onSave={handleAdd} onClose={() => { setEditing(null); setError(null); }} saving={saving} error={error} />}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: theme.color.subtle }}>
          {loading ? null : <>
            {open.length} open request{open.length === 1 ? "" : "s"}
            {closed.length > 0 && ` · ${closed.length} closed`}
          </>}
        </div>
        {canEdit && <button onClick={() => setShowAdd(true)} style={{ ...btnPrimary, cursor: "pointer" }}>+ New Request</button>}
      </div>

      {error && (
        <div style={{ ...card, marginBottom: 12, padding: "10px 14px", background: "var(--c-dangerBg)", color: "var(--c-dangerText)", fontSize: 13 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ ...card, fontSize: 13, color: theme.color.muted }}>Loading…</div>
      ) : dsrEntries.length === 0 ? (
        <div style={{ ...emptyFrame, padding: 40 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 6 }}>No data subject requests yet</div>
          <div style={{ fontSize: 13, color: theme.color.muted }}>
            Track requests from individuals exercising their data protection rights. Requests must be answered within {clockLabel(clocks.rights, "30 days")}{clocks.rightsExtension ? `, extendable once by ${clocks.rightsExtension.label} for complex or numerous requests` : ""}.
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {open.map((entry) => (
            <div key={entry.id} data-deeplink={entry.id} style={card}>
              <div
                style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, cursor: "pointer" }}
                onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading }}>
                      {humanise(entry.type)} request
                    </span>
                    <Pill label={entry.status} colors={STATUS_COLORS[entry.status] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                    {deadlineBadge(entry.dueAt, entry.status)}
                    {entry.extended && (
                      <span style={{ fontSize: 11, color: theme.color.faint }}>Extended</span>
                    )}
                    {checklistSummary(entry.checklist) && (
                      <span style={{ fontSize: 11, color: theme.color.faint }}>{checklistSummary(entry.checklist)}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: theme.color.muted }}>
                    From: <strong style={{ color: theme.color.text }}>{entry.requestorName}</strong>
                    {" · "}Received: {fmtDateOnly(entry.receivedAt)}
                    {" · "}Due: {fmtDateOnly(entry.dueAt)}
                  </div>
                </div>
                <span style={{ color: theme.color.muted, fontSize: 16, marginTop: 2 }}>{expanded === entry.id ? "▲" : "▼"}</span>
              </div>
              {expanded === entry.id && <DsrView entry={entry} busy={actioning === entry.id} onAction={(a) => handleAction(entry.id, a)} onEdit={() => setEditing({ entry })} onChanged={reload} />}
            </div>
          ))}

          {closed.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 8 }}>
                Closed requests
              </div>
              {closed.map((entry) => (
                <div key={entry.id} data-deeplink={entry.id} style={card}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                    onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.muted }}>
                      {humanise(entry.type)} — {entry.requestorName}
                    </span>
                    <Pill label={entry.status} colors={STATUS_COLORS[entry.status] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                    <span style={{ fontSize: 11, color: theme.color.faint, marginLeft: "auto" }}>
                      Due {fmtDateOnly(entry.dueAt)}
                    </span>
                    <span style={{ color: theme.color.muted, fontSize: 16 }}>{expanded === entry.id ? "▲" : "▼"}</span>
                  </div>
                  {expanded === entry.id && <DsrView entry={entry} busy={actioning === entry.id} onAction={(a) => handleAction(entry.id, a)} onEdit={() => setEditing({ entry })} onChanged={reload} />}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── DPIA Tab ───────────────────────────────────────────────────────────────────

interface DpiaFormData {
  title: string;
  description: string;
  ropaEntryId: string;
}

function DpiaAddModal({ ropaEntries, onSave, onClose, saving }: {
  ropaEntries: RopaEntry[];
  onSave: (data: DpiaFormData) => Promise<void>;
  onClose: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<DpiaFormData>({ title: "", description: "", ropaEntryId: "" });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await onSave(form);
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title="New DPIA" onClose={onClose} />
      <form onSubmit={handleSubmit} style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
        <FormField label="Title *">
          <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="e.g. DPIA for new customer analytics system" style={{ ...inputStyle, fontSize: 13 }} />
        </FormField>
        <FormField label="Description">
          <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            rows={3} placeholder="Describe the processing activity and why a DPIA is needed…"
            style={{ ...inputStyle, fontSize: 13, resize: "vertical" }} />
        </FormField>
        {ropaEntries.length > 0 && (
          <FormField label="Link to ROPA Entry (optional)">
            <select value={form.ropaEntryId} onChange={(e) => setForm((f) => ({ ...f, ropaEntryId: e.target.value }))}
              style={{ ...inputStyle, fontSize: 13 }}>
              <option value="">— Not linked —</option>
              {ropaEntries.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </FormField>
        )}
        <div style={{ display: "flex", gap: 10 }}>
          <button type="submit" disabled={saving}
            style={{ ...btnPrimary, opacity: saving ? 0.6 : 1, cursor: saving ? "not-allowed" : "pointer" }}>
            {saving ? "Saving…" : "Create DPIA"}
          </button>
          <button type="button" onClick={onClose}
            style={{ padding: "9px 16px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "transparent", color: theme.color.muted, cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

/** What the assessment recorded against the Art. 35(3) cases and each WP248 criterion. */
function DpiaCriteriaSection({ entry }: { entry: DpiaEntry }) {
  const rec = sanitiseCriteriaRecord(entry.criteria ?? {});
  const cases = (entry.mandatoryCases ?? []).map((id) => ART35_3_CASES.find((c) => c.id === id)?.label ?? id);
  const open = unansweredCriteria(rec).length;
  return (
    <DetailSection title="Screening (GDPR Art. 35(3), WP248)">
      <div style={{ display: "grid", gap: 4, fontSize: 13, color: theme.color.text }}>
        {cases.map((c) => <div key={c}><strong style={{ fontWeight: 600 }}>Mandatory:</strong> {c}</div>)}
        {WP248_CRITERIA.map((c) => {
          const r = rec[c.id];
          return (
            <div key={c.id}>
              {c.n}. {c.label}: <span style={{ color: r ? (r.met ? theme.color.text : theme.color.muted) : "var(--c-warningText)" }}>{r ? (r.met ? "applies" : "does not apply") : "not answered"}</span>
              {r?.note ? <span style={{ color: theme.color.muted }}> — {r.note}</span> : null}
            </div>
          );
        })}
        {open > 0 && <div style={{ fontSize: 12, color: theme.color.muted, marginTop: 4 }}>{open} of 9 criteria not yet answered.</div>}
      </div>
    </DetailSection>
  );
}

/** The assessment's fields: what it covers, what it links to, and the working analysis. */
function DpiaEditModal({ entry, ropaEntries, saving, onSave, onClose }: {
  entry: DpiaEntry;
  ropaEntries: RopaEntry[];
  saving: boolean;
  onSave: (fields: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
}) {
  const links = useLinkOptions();
  const [f, setF] = useState({
    title: entry.title, description: entry.description ?? "", ropaEntryId: entry.ropaEntryId ?? "",
    supplierIds: (entry.suppliers ?? []).map((s) => s.id), systemIds: (entry.systems ?? []).map((s) => s.id),
    linkedRiskIds: entry.linkedRiskIds ?? [],
    necessity: entry.necessity ?? "", risks: entry.risks ?? "", mitigations: entry.mitigations ?? "",
    residualRisk: entry.residualRisk ?? "", dpoConsulted: entry.dpoConsulted, dpoOpinion: entry.dpoOpinion ?? "",
    criteria: sanitiseCriteriaRecord(entry.criteria ?? {}), mandatoryCases: entry.mandatoryCases ?? [],
    reviewAt: toDateInput(entry.reviewAt ?? null),
  });
  const [law, setLaw] = useState<DpiaLawForm>(dpiaLawFrom(entry as unknown as Record<string, unknown>));
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    await onSave({
      ...dpiaLawPayload(law),
      title: f.title, ...(f.description.trim() ? { description: f.description } : {}), ropaEntryId: f.ropaEntryId || null,
      supplierIds: f.supplierIds, systemIds: f.systemIds, linkedRiskIds: f.linkedRiskIds,
      necessity: f.necessity || null, risks: f.risks || null, mitigations: f.mitigations || null,
      ...(f.residualRisk ? { residualRisk: f.residualRisk } : {}),
      dpoConsulted: f.dpoConsulted, dpoOpinion: f.dpoOpinion || null,
      criteria: f.criteria, mandatoryCases: f.mandatoryCases,
      reviewAt: f.reviewAt || null,
    });
  }
  const area = { ...inputStyle, fontSize: 13, resize: "vertical" as const };
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={`Edit assessment: ${entry.title}`} onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <FormField label="Title *" span2>
          <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} style={{ ...inputStyle, fontSize: 13 }} />
        </FormField>
        <FormField label="Description" span2>
          <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} style={area} />
        </FormField>
        <FormField label="Processing activity" span2>
          <select value={f.ropaEntryId} onChange={(e) => setF({ ...f, ropaEntryId: e.target.value })} style={{ ...inputStyle, fontSize: 13 }}>
            <option value="">— Not linked —</option>
            {f.ropaEntryId && !ropaEntries.some((r) => r.id === f.ropaEntryId) && <option value={f.ropaEntryId}>{entry.ropaEntryName ?? "Linked activity"}</option>}
            {ropaEntries.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </FormField>
        <FormField label="Suppliers in scope" span2>
          <LinkPicker options={links.suppliers} selected={f.supplierIds} onChange={(v) => setF({ ...f, supplierIds: v })}
            empty="No suppliers recorded yet." />
        </FormField>
        <FormField label="Systems in scope" span2>
          <LinkPicker options={links.assets} selected={f.systemIds} onChange={(v) => setF({ ...f, systemIds: v })}
            empty="No assets recorded yet." />
        </FormField>
        <FormField label="Linked risks" span2>
          <LinkPicker options={links.risks} selected={f.linkedRiskIds} onChange={(v) => setF({ ...f, linkedRiskIds: v })}
            empty="No risks in the risk register yet." />
        </FormField>
        <FormField label="Screening against the nine WP248 criteria" span2>
          <DpiaCriteriaFields value={f.criteria} cases={f.mandatoryCases}
            onChange={(criteria) => setF({ ...f, criteria })} onCasesChange={(mandatoryCases) => setF({ ...f, mandatoryCases })} />
        </FormField>
        <FormField label="Necessity" span2>
          <textarea value={f.necessity} onChange={(e) => setF({ ...f, necessity: e.target.value })} rows={2} placeholder="Why is this processing necessary and proportionate?" style={area} />
        </FormField>
        <FormField label="Risks identified" span2>
          <textarea value={f.risks} onChange={(e) => setF({ ...f, risks: e.target.value })} rows={2} placeholder="What risks to individuals have been identified?" style={area} />
        </FormField>
        <FormField label="Mitigations" span2>
          <textarea value={f.mitigations} onChange={(e) => setF({ ...f, mitigations: e.target.value })} rows={2} placeholder="What measures are in place to mitigate risks?" style={area} />
        </FormField>
        <FormField label="Residual risk">
          <select value={f.residualRisk} onChange={(e) => setF({ ...f, residualRisk: e.target.value })} style={{ ...inputStyle, fontSize: 13 }}>
            <option value="">— Select —</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="unknown">Unknown</option>
          </select>
        </FormField>
        <FormField label="Next review">
          <input type="date" value={f.reviewAt} onChange={(e) => setF({ ...f, reviewAt: e.target.value })} style={{ ...inputStyle, fontSize: 13 }} />
        </FormField>
        <FormField label="DPO consulted">
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 400, marginTop: 6 }}>
            <input type="checkbox" checked={f.dpoConsulted} onChange={(e) => setF({ ...f, dpoConsulted: e.target.checked })} />
            DPO has been consulted
          </label>
        </FormField>
        {f.dpoConsulted && (
          <FormField label="DPO opinion" span2>
            <textarea value={f.dpoOpinion} onChange={(e) => setF({ ...f, dpoOpinion: e.target.value })} rows={2} placeholder="Summary of DPO's opinion…" style={area} />
          </FormField>
        )}
        <DpiaLawFields value={law} onChange={(p) => setLaw((l) => ({ ...l, ...p }))} dpoConsulted={f.dpoConsulted} residualRisk={f.residualRisk} />
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
          <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1, cursor: saving ? "not-allowed" : "pointer" }}>
            {saving ? "Saving…" : "Save changes"}
          </button>
          <button type="button" onClick={onClose} style={{ ...btnSecondary }}>Cancel</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

function DpiaTab({ dpiaEntries, ropaEntries, loading, reload, initialOpen }: {
  dpiaEntries: DpiaEntry[];
  ropaEntries: RopaEntry[];
  loading: boolean;
  reload: () => void;
  initialOpen?: string | null;
}) {
  const canEdit = useCanEdit();
  const { user } = useCurrentUser();
  const linkOptions = useLinkOptions();
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(initialOpen ?? null);
  useDeepLinkScroll(initialOpen, !loading);
  const [editing, setEditing] = useState<DpiaEntry | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleAdd(data: DpiaFormData) {
    setSaving(true);
    setError(null);
    try {
      await api.post("/gdpr/dpia", {
        title: data.title,
        description: data.description || null,
        ropaEntryId: data.ropaEntryId || null,
      });
      setShowAdd(false);
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleStatusChange(id: string, status: string) {
    try {
      await api.patch(`/gdpr/dpia/${id}`, { status });
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to update");
    }
  }

  async function handleDelete(entry: DpiaEntry) {
    if (!confirm(`Delete the impact assessment "${entry.title}"? This cannot be undone.`)) return;
    try {
      await api.del(`/gdpr/dpia/${entry.id}`);
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to delete");
    }
  }

  async function handleSaveFields(id: string, fields: Record<string, unknown>) {
    setSaving(true);
    try {
      await api.patch(`/gdpr/dpia/${id}`, fields);
      // Back to the assessment's view, which stays expanded.
      setEditing(null);
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {showAdd && <DpiaAddModal ropaEntries={ropaEntries} onSave={handleAdd} onClose={() => setShowAdd(false)} saving={saving} />}
      {editing && <DpiaEditModal key={editing.id} entry={editing} ropaEntries={ropaEntries} saving={saving} onSave={(f) => handleSaveFields(editing.id, f)} onClose={() => setEditing(null)} />}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: theme.color.subtle }}>
          {dpiaEntries.length} assessment{dpiaEntries.length === 1 ? "" : "s"}
        </div>
        {canEdit && <button onClick={() => setShowAdd(true)} style={{ ...btnPrimary, cursor: "pointer" }}>+ New DPIA</button>}
      </div>

      {error && (
        <div style={{ ...card, marginBottom: 12, padding: "10px 14px", background: "var(--c-dangerBg)", color: "var(--c-dangerText)", fontSize: 13 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ ...card, fontSize: 13, color: theme.color.muted }}>Loading…</div>
      ) : dpiaEntries.length === 0 ? (
        <div style={{ ...emptyFrame, padding: 40 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 6 }}>No DPIAs yet</div>
          <div style={{ fontSize: 13, color: theme.color.muted }}>
            A Data Protection Impact Assessment is required before starting processing that is likely to result in high risk to individuals (GDPR Article 35).
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {dpiaEntries.map((entry) => (
            <div key={entry.id} data-deeplink={entry.id} style={card}>
              <div
                style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, cursor: "pointer" }}
                onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading, marginBottom: 4 }}>{entry.title}</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <Pill label={entry.status} colors={STATUS_COLORS[entry.status] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                    {entry.residualRisk && (
                      <Pill label={`Risk: ${entry.residualRisk}`} colors={RISK_COLORS[entry.residualRisk] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                    )}
                    {entry.dpoConsulted && (
                      <span style={{ fontSize: 11, color: "var(--c-successText)", fontWeight: 600 }}>DPO consulted</span>
                    )}
                    {entry.ropaEntryName && (
                      <span style={{ fontSize: 11, color: theme.color.faint }}>Linked to: {entry.ropaEntryName}</span>
                    )}
                  </div>
                </div>
                <span style={{ color: theme.color.muted, fontSize: 16, marginTop: 2 }}>{expanded === entry.id ? "▲" : "▼"}</span>
              </div>

              {expanded === entry.id && (
                <CardView>
                  <ViewHead tags={<EntityTagsRow entity={{ kind: "dpia", id: entry.id }} readOnly={!canEdit} />} onEdit={canEdit ? () => setEditing(entry) : undefined} />
                  <NextSteps items={dpiaSteps(entry)} />
                  <DetailSection title="The processing">
                    <DetailFields fields={[
                      { label: "Description", value: entry.description, wide: true, multiline: true },
                      { label: "Processing activity", value: entry.ropaEntryName },
                      { label: "Status", value: humanise(entry.status) },
                      { label: "Suppliers in scope", value: linkNames(entry.suppliers), wide: true },
                      { label: "Systems in scope", value: linkNames(entry.systems), wide: true },
                      { label: "AI systems covered", value: entry.aiSystems?.length ? entry.aiSystems.map((a) => `${a.name} (${humanise(a.riskClass)} risk)`).join(", ") : null, wide: true },
                      { label: "Linked risks", value: (entry.linkedRiskIds ?? []).length ? (entry.linkedRiskIds ?? []).map((id) => linkOptions.risks.find((r) => r.id === id)?.label ?? "Risk").join(", ") : null, wide: true },
                    ]} />
                  </DetailSection>
                  <DpiaCriteriaSection entry={entry} />
                  <DetailSection title="Assessment">
                    <DetailFields fields={[
                      { label: "Necessity and proportionality", value: entry.necessity, wide: true, multiline: true },
                      { label: "Risks identified", value: entry.risks, wide: true, multiline: true },
                      { label: "Mitigations", value: entry.mitigations, wide: true, multiline: true },
                      { label: "Residual risk", value: entry.residualRisk ? humanise(entry.residualRisk) : null },
                      { label: "DPO consulted", value: entry.dpoConsulted ? "Yes" : "No" },
                      ...(entry.dpoConsulted ? [{ label: "DPO opinion", value: entry.dpoOpinion, wide: true, multiline: true }] : []),
                      ...(entry.dpoConsulted && entry.dpoAdviceFollowed != null ? [{ label: "DPO advice followed", value: entry.dpoAdviceFollowed ? "Yes" : `No: ${entry.dpoDisagreementReason ?? "reason not recorded"}`, wide: true }] : []),
                      { label: "Data subjects' views", value: entry.subjectViewsSought == null ? null : entry.subjectViewsSought ? entry.subjectViews : `Not sought: ${entry.subjectViewsNotSoughtReason ?? "reason not recorded"}`, wide: true, multiline: true },
                      ...(entry.residualRisk === "high" ? [{ label: "Prior consultation (Art. 36)", value: priorConsultationText(entry), wide: true }] : []),
                    ]} />
                  </DetailSection>
                  <DetailSection title="Review">
                    <DetailFields fields={[
                      { label: "Submitted for review", value: entry.submittedAt ? `${fmtDate(entry.submittedAt)}${entry.submittedByName ? ` by ${entry.submittedByName}` : ""}` : null },
                      { label: "Approved", value: entry.status === "approved" && entry.approvedAt ? `${fmtDate(entry.approvedAt)}${entry.approvedByName ? ` by ${entry.approvedByName}` : ""}` : null },
                    ]} />
                  </DetailSection>
                  {canEdit && (() => {
                    // The review workflow (backend lib/dpiaWorkflow.ts): the server refuses anything else.
                    const missing = dpiaMissingForApproval(entry);
                    const ownSubmission = !!user?.id && entry.submittedById === user.id;
                    return (
                    <RecordActions>
                      {entry.status === "draft" && (
                        <button type="button" onClick={() => handleStatusChange(entry.id, "in_review")} style={actionBtn}>Submit for review</button>
                      )}
                      {entry.status === "in_review" && (
                        <>
                          <button type="button" disabled={missing.length > 0} onClick={() => handleStatusChange(entry.id, "approved")} style={actionBtn}
                            title={missing.length ? `Complete the assessment first: ${missing.join(", ")}` : ownSubmission ? "You submitted this assessment; another member must approve it unless you are the only member" : undefined}>
                            Approve
                          </button>
                          <button type="button" onClick={() => handleStatusChange(entry.id, "rejected")} style={actionDangerBtn}>Reject</button>
                          <button type="button" onClick={() => handleStatusChange(entry.id, "draft")} style={actionBtn}>Return to draft</button>
                        </>
                      )}
                      {entry.status === "rejected" && (
                        <button type="button" onClick={() => handleStatusChange(entry.id, "draft")} style={actionBtn}>Revise (back to draft)</button>
                      )}
                      {entry.status === "approved" && (
                        <button type="button" onClick={() => handleStatusChange(entry.id, "in_review")} style={actionBtn}>Reopen for review</button>
                      )}
                      {entry.status !== "approved" && (
                        <button type="button" onClick={() => handleDelete(entry)} style={actionDangerBtn}>Delete assessment</button>
                      )}
                      {entry.status === "in_review" && missing.length > 0 && (
                        <span style={text.hint}>Before it can be approved, record: {missing.join(", ")}.</span>
                      )}
                    </RecordActions>
                    );
                  })()}
                </CardView>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Breaches Tab ───────────────────────────────────────────────────────────────

interface BreachFormData {
  title: string;
  description: string;
  discoveredAt: string;
  severity: string;
  notifiedDpaRequired: boolean;
  breachTypes: string[];
  dataCategories: string[];
  affectedCount: string;
  remediation: string;
  notes: string;
  supplierId: string;
  assetIds: string[];
  linkedRiskId: string;
  ropaEntryIds: string[];
  /** Art. 33(2), (3), (5), 34(3) (components/DpRegisterFields.tsx). */
  law: BreachLawForm;
}

/** Reports a breach, or (given `initial`) edits one. */
function BreachAddModal({ onSave, onClose, saving, initial, error }: {
  onSave: (data: BreachFormData) => Promise<void>;
  onClose: () => void;
  saving: boolean;
  initial?: BreachEntry;
  error?: string | null;
}) {
  const dp = useDp();
  const links = useLinkOptions();
  // The box is local time, so its default is local now (never the UTC clock, an hour behind in summer).
  const now = toLocalInput(new Date().toISOString());
  const authorities = initial ? breachAuthorities(initial, dp.clocks, dp.authorityPhrase) : breachAuthorities({} as BreachEntry, dp.clocks, dp.authorityPhrase);
  // The time the organisation became aware (stored as discoveredAt) is fixed with the first notification.
  const discoveryLocked = !!(initial?.notifiedDpaAt || initial?.notifiedSubjectsAt);
  const [form, setForm] = useState<BreachFormData>(initial ? {
    title: initial.title, description: initial.description ?? "", discoveredAt: toLocalInput(initial.discoveredAt),
    severity: initial.severity, notifiedDpaRequired: initial.notifiedDpaRequired, breachTypes: initial.breachTypes,
    dataCategories: initial.dataCategories, affectedCount: initial.affectedCount === null ? "" : String(initial.affectedCount),
    remediation: initial.remediation ?? "", notes: initial.notes ?? "",
    supplierId: initial.supplierId ?? "", assetIds: (initial.assets ?? []).map((a) => a.id), linkedRiskId: initial.linkedRiskId ?? "",
    ropaEntryIds: (initial.ropaEntries ?? []).map((r) => r.id),
    law: breachLawFrom(initial as unknown as Record<string, unknown>),
  } : {
    title: "", description: "", discoveredAt: now, severity: "medium",
    notifiedDpaRequired: false, breachTypes: [], dataCategories: [],
    affectedCount: "", remediation: "", notes: "", supplierId: "", assetIds: [], linkedRiskId: "", ropaEntryIds: [],
    law: breachLawFrom({}),
  });
  const agreements = useAgreementOptions();
  // Whether notification was required is recorded with the notification itself, and final with it.
  const requiredLocked = !!initial?.notifiedDpaAt;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await onSave(form);
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={initial ? "Edit breach" : "Report Data Breach"} onClose={onClose} />
      <form onSubmit={handleSubmit} style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <FormField label="Title *" span2>
          <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Brief description of the breach" style={{ ...inputStyle, fontSize: 13 }} />
        </FormField>
        <FormField label="Description *" span2>
          <textarea required value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            rows={3} placeholder="Describe what happened…" style={{ ...inputStyle, fontSize: 13, resize: "vertical" }} />
        </FormField>
        <FormField label="Became aware at *">
          <input type="datetime-local" required value={form.discoveredAt} max={now} disabled={discoveryLocked}
            title={discoveryLocked ? "Fixed once someone has been notified: every deadline is measured from it" : "When you became reasonably certain that a security incident compromised personal data (GDPR Art. 33(1); EDPB Guidelines 9/2022 para. 31) — a short investigation before that is allowed. Every deadline runs from here."}
            onChange={(e) => setForm((f) => ({ ...f, discoveredAt: e.target.value }))}
            style={{ ...inputStyle, fontSize: 13 }} />
        </FormField>
        <FormField label="Severity *">
          <select value={form.severity} onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </FormField>
        <FormField label="Breach Types" span2>
          <MultiSelect options={BREACH_TYPES} selected={form.breachTypes} onChange={(v) => setForm((f) => ({ ...f, breachTypes: v }))} />
        </FormField>
        <FormField label="Data Categories Affected" span2>
          <DataCategoryPicker selected={form.dataCategories} onChange={(v) => setForm((f) => ({ ...f, dataCategories: v }))} />
        </FormField>
        <FormField label="Approx. Affected Individuals">
          <input type="number" min={0} value={form.affectedCount}
            onChange={(e) => setForm((f) => ({ ...f, affectedCount: e.target.value }))}
            placeholder="e.g. 250" style={{ ...inputStyle, fontSize: 13 }} />
        </FormField>
        <FormField label={`${authorityNoun(dp.authorityPhrase)} notification required?`}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: requiredLocked ? "default" : "pointer", fontWeight: 400, marginTop: 6 }}>
            <input type="checkbox" checked={form.notifiedDpaRequired} disabled={requiredLocked}
              onChange={(e) => setForm((f) => ({ ...f, notifiedDpaRequired: e.target.checked }))} />
            {requiredLocked
              ? `Notified ${fmtDate(initial!.notifiedDpaAt!)} — recorded and final`
              : `Notify ${authorities.map((a) => `${a.authority} (${a.hours !== null ? a.label ?? `${a.hours} hours` : a.label ?? "no fixed deadline"})`).join(", ")}`}
          </label>
        </FormField>
        <FormField label="Processor where it happened">
          <select value={form.supplierId} onChange={(e) => setForm((f) => ({ ...f, supplierId: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }}>
            <option value="">— Not at a processor —</option>
            {form.supplierId && !links.suppliers.some((s) => s.id === form.supplierId) && <option value={form.supplierId}>{initial?.supplier?.name ?? "Linked supplier"}</option>}
            {links.suppliers.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </FormField>
        <FormField label="Risk it materialised">
          <select value={form.linkedRiskId} onChange={(e) => setForm((f) => ({ ...f, linkedRiskId: e.target.value }))} style={{ ...inputStyle, fontSize: 13 }}>
            <option value="">— None —</option>
            {form.linkedRiskId && !links.risks.some((r) => r.id === form.linkedRiskId) && <option value={form.linkedRiskId}>Linked risk</option>}
            {links.risks.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </FormField>
        <FormField label="Assets affected" span2>
          <LinkPicker options={links.assets} selected={form.assetIds} onChange={(v) => setForm((f) => ({ ...f, assetIds: v }))}
            empty="No assets recorded yet." />
        </FormField>
        <FormField label="Processing activities whose data was involved" span2>
          <LinkPicker options={links.ropa} selected={form.ropaEntryIds} onChange={(v) => setForm((f) => ({ ...f, ropaEntryIds: v }))}
            empty="No processing activities recorded yet." />
        </FormField>
        <FormField label="Remediation steps" span2>
          <textarea value={form.remediation} onChange={(e) => setForm((f) => ({ ...f, remediation: e.target.value }))}
            rows={2} placeholder="What steps are being taken to address the breach?"
            style={{ ...inputStyle, fontSize: 13, resize: "vertical" }} />
        </FormField>
        <BreachLawFields
          value={form.law} onChange={(p) => setForm((f) => ({ ...f, law: { ...f.law, ...p } }))}
          notifiedDpaRequired={form.notifiedDpaRequired} severity={form.severity}
          lockedController={!!initial?.controllerNotifiedAt} agreements={agreements}
        />
        <FormField label="Notes" span2>
          <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={2} placeholder="Any additional context…" style={{ ...inputStyle, fontSize: 13, resize: "vertical" }} />
        </FormField>
        {error && <ModalError message={error} />}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
          <button type="submit" disabled={saving}
            style={{ ...btnPrimary, opacity: saving ? 0.6 : 1, cursor: saving ? "not-allowed" : "pointer" }}>
            {saving ? "Saving…" : initial ? "Save changes" : "Report Breach"}
          </button>
          <button type="button" onClick={onClose}
            style={{ padding: "9px 16px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "transparent", color: theme.color.muted, cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

/** Authority notification overdue — the strictest breach clock among the laws in scope (72 hours where none fixes one). */
function isAuthorityOverdue(entry: BreachEntry, authorityHours: number): boolean {
  if (!entry.notifiedDpaRequired) return false;
  if (entry.notifiedDpaAt) return false;
  // Told through a linked incident's notification or submission: not overdue, whatever the breach says.
  if (entry.authorityNotice) return false;
  if (entry.status === "closed") return false;
  const elapsed = Date.now() - new Date(entry.discoveredAt).getTime();
  return elapsed > authorityHours * 60 * 60 * 1000;
}

/**
 * Where the breach stands with each authority and with the people affected. Each authority runs
 * on its own law's breach clock, counted from becoming aware; the overdue warning above the register
 * uses the strictest of them.
 */
const notificationRow: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "4px 16px", alignItems: "baseline" };

function BreachNotifications({ entry, canEdit, onRecord }: { entry: BreachEntry; canEdit: boolean; onRecord: (authority: string, at: string) => Promise<void> }) {
  const dp = useDp();
  const [when, setWhen] = useState<Record<string, string>>({});
  const authorities = breachAuthorities(entry, dp.clocks, dp.authorityPhrase);
  const notices = entry.authorityNotices ?? [];
  // A breach notified before each authority was recorded separately: the date stands, the authority is unknown.
  const legacyDate = entry.notifiedDpaAt && notices.length === 0 ? entry.notifiedDpaAt : null;
  const nowLocal = toLocalInput(new Date().toISOString());
  return (
    <DetailSection title="Notifications">
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {authorities.map((a) => {
          const notice = notices.find((n) => n.authority === a.authority) ?? null;
          const dueAt = a.hours !== null ? new Date(new Date(entry.discoveredAt).getTime() + a.hours * 3_600_000).toISOString() : null;
          const overdue = entry.notifiedDpaRequired && !notice && !legacyDate && !entry.authorityNotice && entry.status !== "closed"
            && dueAt !== null && Date.now() > new Date(dueAt).getTime();
          const via = toldViaIncident(entry);
          const standing = !entry.notifiedDpaRequired
            ? "Not required (recorded as not needing notification)"
            : notice
              ? `Notified ${fmtDateTime(notice.notifiedAt)}`
              : legacyDate ? `A notification was recorded ${fmtDateTime(legacyDate)}; which authority was told was not recorded`
              : via ? `Told ${fmtDateTime(via.at)} through ${via.incidentRef ?? "the linked incident"}`
              : overdue ? "Overdue, not yet notified" : "Not yet notified";
          const canRecord = canEdit && entry.notifiedDpaRequired && !notice;
          return (
          <div key={`${a.regulation}-${a.authority}`} style={notificationRow}>
            <div>
              <div style={{ ...text.body, fontWeight: 600 }}>{authorityNoun(a.authority)}</div>
              {a.regulation && <div style={text.meta}>{a.regulation}</div>}
            </div>
            <div>
              <div style={text.label}>Due</div>
              <div style={text.body}>
                {!entry.notifiedDpaRequired ? "—"
                  : dueAt ? `${fmtDateTime(dueAt)} (${a.label ?? `${a.hours} hours`})`
                  : `No fixed deadline${a.label ? ` (${a.label})` : ""}`}
              </div>
            </div>
            <div>
              <div style={text.label}>Standing</div>
              <div style={{ ...text.body, color: overdue ? "var(--c-dangerText)" : notice || via ? "var(--c-successText)" : text.body.color, fontWeight: overdue ? 600 : undefined }}>{standing}</div>
              {canRecord && (
                <span style={{ display: "inline-flex", gap: 6, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
                  <input type="datetime-local" aria-label={`When ${authorityNoun(a.authority)} was notified`} value={when[a.authority] ?? ""}
                    min={toLocalInput(entry.discoveredAt)} max={nowLocal}
                    onChange={(e) => setWhen((p) => ({ ...p, [a.authority]: e.target.value }))}
                    style={{ ...inputStyle, fontSize: 12, width: 200 }} />
                  <button type="button" disabled={!when[a.authority]} style={actionBtn}
                    onClick={() => {
                      const v = when[a.authority];
                      if (v && confirm(`Record that ${a.authority} was notified on ${fmtDateTime(new Date(v).toISOString())}? It cannot be changed afterwards.`)) {
                        void onRecord(a.authority, new Date(v).toISOString());
                      }
                    }}>
                    Record notification
                  </button>
                </span>
              )}
            </div>
          </div>
          );
        })}
        <div style={notificationRow}>
          <div style={{ ...text.body, fontWeight: 600 }}>People affected</div>
          <div>
            <div style={text.label}>Due</div>
            <div style={text.body}>{dp.clocks.breachSubjects ? clockLabel(dp.clocks.breachSubjects, "") : "Without undue delay where the risk to them is high"}</div>
          </div>
          <div>
            <div style={text.label}>Standing</div>
            <div style={{ ...text.body, color: entry.notifiedSubjectsAt ? "var(--c-successText)" : text.body.color }}>{entry.notifiedSubjectsAt ? `Notified ${fmtDateTime(entry.notifiedSubjectsAt)}` : "Not recorded"}</div>
          </div>
        </div>
      </div>
    </DetailSection>
  );
}

function BreachesTab({ breachEntries, loading, reload, initialOpen }: { breachEntries: BreachEntry[]; loading: boolean; reload: () => void; initialOpen?: string | null }) {
  const dp = useDp();
  const authorityHours = dp.clocks.breachAuthority?.value ?? 72;
  const authority = authorityNoun(dp.authorityPhrase);
  const canEdit = useCanEdit();
  const linkOptions = useLinkOptions();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<BreachEntry | null>(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(initialOpen ?? null);
  useDeepLinkScroll(initialOpen, !loading);
  const [error, setError] = useState<string | null>(null);
  const [notifySubjectDate, setNotifySubjectDate] = useState<Record<string, string>>({});

  async function handleEdit(data: BreachFormData) {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      const discovered = new Date(data.discoveredAt);
      // Sent only when changed: the box holds minutes, the record may hold seconds.
      const discoveredChanged = data.discoveredAt !== toLocalInput(editing.discoveredAt) && !isNaN(discovered.getTime());
      await api.patch(`/gdpr/breaches/${editing.id}`, {
        title: data.title,
        ...(data.description ? { description: data.description } : {}),
        ...(discoveredChanged ? { discoveredAt: discovered.toISOString() } : {}),
        severity: data.severity,
        notifiedDpaRequired: data.notifiedDpaRequired,
        breachTypes: data.breachTypes,
        dataCategories: data.dataCategories,
        affectedCount: data.affectedCount ? parseInt(data.affectedCount, 10) : null,
        remediation: data.remediation || null,
        notes: data.notes || null,
        supplierId: data.supplierId || null,
        assetIds: data.assetIds,
        linkedRiskId: data.linkedRiskId || null,
        ropaEntryIds: data.ropaEntryIds,
        ...breachLawPayload(data.law, !!editing.controllerNotifiedAt),
      });
      // Back to the breach's view, which stays expanded.
      setEditing(null);
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleAdd(data: BreachFormData) {
    setSaving(true);
    setError(null);
    try {
      const { law, ...fields } = data;
      await api.post("/gdpr/breaches", {
        ...fields,
        ...breachLawPayload(law, false),
        // The API takes an ISO date-time with its offset; the input gives local "YYYY-MM-DDTHH:mm".
        discoveredAt: data.discoveredAt ? new Date(data.discoveredAt).toISOString() : data.discoveredAt,
        // Required by the API: send what was typed, so a blank one is refused with its own message.
        description: data.description,
        affectedCount: data.affectedCount ? parseInt(data.affectedCount, 10) : null,
        remediation: data.remediation || null,
        notes: data.notes || null,
        supplierId: data.supplierId || null,
        linkedRiskId: data.linkedRiskId || null,
      });
      setShowAdd(false);
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(entry: BreachEntry) {
    if (!confirm(`Delete the breach record "${entry.title}"? This cannot be undone.`)) return;
    try {
      await api.del(`/gdpr/breaches/${entry.id}`);
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to delete");
    }
  }

  async function handleRecordAuthority(id: string, authority: string, notifiedAt: string) {
    setError(null);
    try {
      await api.post(`/gdpr/breaches/${id}/authority-notices`, { authority, notifiedAt });
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to record the notification");
    }
  }

  async function handleUpdate(id: string, patch: Partial<BreachEntry>) {
    try {
      await api.patch(`/gdpr/breaches/${id}`, patch);
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to update");
    }
  }

  return (
    <div>
      {showAdd && <BreachAddModal onSave={handleAdd} onClose={() => { setShowAdd(false); setError(null); }} saving={saving} error={error} />}
      {editing && <BreachAddModal key={editing.id} initial={editing} onSave={handleEdit} onClose={() => { setEditing(null); setError(null); }} saving={saving} error={error} />}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: theme.color.subtle }}>
          {breachEntries.length} breach{breachEntries.length === 1 ? "" : "es"} logged
        </div>
        {canEdit && <button onClick={() => setShowAdd(true)} style={{ ...btnPrimary, cursor: "pointer" }}>+ Report Breach</button>}
      </div>

      {error && (
        <div style={{ ...card, marginBottom: 12, padding: "10px 14px", background: "var(--c-dangerBg)", color: "var(--c-dangerText)", fontSize: 13 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ ...card, fontSize: 13, color: theme.color.muted }}>Loading…</div>
      ) : breachEntries.length === 0 ? (
        <div style={{ ...emptyFrame, padding: 40 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 6 }}>No breaches logged</div>
          <div style={{ fontSize: 13, color: theme.color.muted }}>
            Record and manage personal data breaches. Serious breaches must be reported to {dp.authorityPhrase} within {clockLabel(dp.clocks.breachAuthority, "72 hours", "of becoming aware")}.
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {breachEntries.map((entry) => (
            <div key={entry.id} data-deeplink={entry.id} style={card}>
              {isAuthorityOverdue(entry, authorityHours) && (
                <div style={{ marginBottom: 12, padding: "8px 12px", background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)", borderRadius: 3, fontSize: 12, fontWeight: 600, color: "var(--c-dangerText)" }}>
                  {authority} notification overdue — this breach required notification within {clockLabel(dp.clocks.breachAuthority, "72 hours", "of becoming aware")}
                </div>
              )}

              <div
                style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, cursor: "pointer" }}
                onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading, marginBottom: 4 }}>{entry.title}</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <Pill label={entry.severity} colors={SEVERITY_COLORS[entry.severity] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                    <Pill label={entry.status} colors={STATUS_COLORS[entry.status] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                    <span style={{ fontSize: 11, color: theme.color.faint }}>Aware: {fmtDate(entry.discoveredAt)}</span>
                    {entry.affectedCount !== null && (
                      <span style={{ fontSize: 11, color: theme.color.muted }}>{entry.affectedCount.toLocaleString()} affected</span>
                    )}
                    {entry.notifiedDpaRequired && entry.notifiedDpaAt && (
                      <span style={{ fontSize: 11, color: "var(--c-successText)", fontWeight: 600 }}>{notifiedAuthorityNames(entry) ?? "Authority"} notified</span>
                    )}
                    {entry.notifiedDpaRequired && toldViaIncident(entry) && (
                      <span style={{ fontSize: 11, color: "var(--c-successText)", fontWeight: 600 }}>{authority} told through {toldViaIncident(entry)!.incidentRef ?? "the linked incident"}</span>
                    )}
                    {entry.notifiedDpaRequired && !entry.notifiedDpaAt && !entry.authorityNotice && (
                      <span style={{ fontSize: 11, color: "var(--c-warningText)", fontWeight: 600 }}>{authority} notification required</span>
                    )}
                    {entry.linkedIncidents && entry.linkedIncidents.length > 0 && (
                      <span style={{ fontSize: 11 }}><IncidentLinks incidents={entry.linkedIncidents} /></span>
                    )}
                  </div>
                </div>
                <span style={{ color: theme.color.muted, fontSize: 16, marginTop: 2 }}>{expanded === entry.id ? "▲" : "▼"}</span>
              </div>

              {expanded === entry.id && (
                <CardView>
                  <ViewHead tags={<EntityTagsRow entity={{ kind: "breach", id: entry.id }} readOnly={!canEdit} />} onEdit={canEdit ? () => setEditing(entry) : undefined} />
                  <NextSteps items={breachSteps(entry, authorityHours)} />
                  <DetailSection title="What happened">
                    <DetailFields fields={[
                      { label: "Description", value: entry.description, wide: true, multiline: true },
                      { label: "Breach types", value: humaniseList(entry.breachTypes) },
                      { label: "Data categories", value: entry.dataCategories.length ? entry.dataCategories.map(dataCategoryLabel).join(", ") : null },
                      { label: "People affected", value: entry.affectedCount === null ? null : entry.affectedCount.toLocaleString("en-GB") },
                      { label: "Became aware", value: fmtDateTime(entry.discoveredAt) },
                      { label: "Severity", value: humanise(entry.severity) },
                      { label: "Status", value: humanise(entry.status) },
                      { label: "Processor", value: entry.supplier?.name ?? null },
                      { label: "Risk it materialised", value: entry.linkedRiskId ? linkOptions.risks.find((r) => r.id === entry.linkedRiskId)?.label ?? "Linked risk" : null },
                      { label: "Assets affected", value: linkNames(entry.assets), wide: true },
                      { label: "Processing activities", value: linkNames(entry.ropaEntries), wide: true },
                      { label: entry.linkedIncidents && entry.linkedIncidents.length > 1 ? "Cyber incidents" : "Cyber incident", value: entry.linkedIncidents?.length ? <IncidentLinks incidents={entry.linkedIncidents} /> : null, wide: true },
                      { label: "Remediation", value: entry.remediation, wide: true, multiline: true },
                      { label: "Notes", value: entry.notes, wide: true, multiline: true },
                    ]} />
                  </DetailSection>
                  <BreachNotifications entry={entry} canEdit={canEdit} onRecord={(a, at) => handleRecordAuthority(entry.id, a, at)} />
                  {toldViaIncident(entry) && (
                    <div style={{ ...text.hint, marginTop: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ flex: "1 1 280px" }}>
                        {authorityNoticeText(toldViaIncident(entry)!, authority)}.
                        {canEdit && " Recording it on the breach makes it the breach's notification date, which cannot be changed afterwards."}
                      </span>
                      {canEdit && (
                        <button type="button" style={actionBtn}
                          onClick={() => { const n = toldViaIncident(entry); if (n && confirm(`Record ${fmtDateTime(n.at)} as the date ${authority} was notified of this breach? It cannot be changed afterwards.`)) handleUpdate(entry.id, { notifiedDpaAt: n.at }); }}>
                          Record this date on the breach
                        </button>
                      )}
                    </div>
                  )}
                  {breachNotified(entry) && (
                    <div style={{ ...text.hint, marginTop: 8 }}>
                      {[entry.notifiedDpaAt ? `${notifiedAuthorityNames(entry) ?? "An authority"} notified ${fmtDate(entry.notifiedDpaAt)}` : null, entry.notifiedSubjectsAt ? `people affected notified ${fmtDate(entry.notifiedSubjectsAt)}` : null].filter(Boolean).join("; ")}.
                      {" "}A recorded notification is final: its date cannot be changed and the breach cannot be deleted. Record a correction in the notes; who recorded it is in the audit log.
                    </div>
                  )}
                  {canEdit && (!breachNotified(entry) || entry.status !== "closed" || !entry.notifiedSubjectsAt) && (
                    <RecordActions>
                      {!breachNotified(entry) && (
                        <button type="button" onClick={() => handleDelete(entry)} style={actionDangerBtn}>Delete breach</button>
                      )}
                      {entry.status !== "closed" && (
                        <button type="button" onClick={() => handleUpdate(entry.id, { status: "closed" })} style={actionBtn}>Mark closed</button>
                      )}
                      {entry.status !== "reported_to_dpa" && entry.status !== "closed" && (
                        <button type="button" onClick={() => handleUpdate(entry.id, { status: "reported_to_dpa" })} style={actionBtn}>Mark reported to {authority}</button>
                      )}
                      {!entry.notifiedSubjectsAt && (
                        <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                          {/* Date and time: the deadline is counted in hours from becoming aware. */}
                          <input type="datetime-local" aria-label="When the people affected were notified" value={notifySubjectDate[entry.id] ?? ""}
                            min={toLocalInput(entry.discoveredAt)} max={toLocalInput(new Date().toISOString())}
                            onChange={(e) => setNotifySubjectDate((p) => ({ ...p, [entry.id]: e.target.value }))}
                            style={{ ...inputStyle, fontSize: 12, width: 200 }} />
                          <button type="button" disabled={!notifySubjectDate[entry.id]}
                            onClick={() => { if (notifySubjectDate[entry.id]) handleUpdate(entry.id, { notifiedSubjectsAt: new Date(notifySubjectDate[entry.id]).toISOString() }); }}
                            style={actionBtn}>
                            Record subject notification
                          </button>
                        </span>
                      )}
                    </RecordActions>
                  )}
                </CardView>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function GdprPage() {
  // Inside the consultant's client portal, navigation is page state rather than a URL.
  const navigate = usePortalNavigate();
  const { user } = useCurrentUser();
  const canEdit = !!user?.permissions?.includes("privacy:write");
  // Each register has its own address (/gdpr/ropa, /gdpr/dsr, /gdpr/dpia, /gdpr/breaches) — the
  // sidebar lists them as separate rows under Laws & privacy. Plain /gdpr picks the tab below.
  const { pathname, search } = useLocation();
  // Deep links from a tag's cross-reference (components/EntityTags.tsx): ?tab=<register>&<kind>=<id>
  // open that register with the record's view expanded.
  const query = new URLSearchParams(search);
  const queryTab = query.get("tab");
  const pathTab = (pathname.match(/\/gdpr\/(ropa|dsr|dpia|breaches|agreements)\/?$/)?.[1]
    ?? (queryTab && ["ropa", "dsr", "dpia", "breaches", "agreements"].includes(queryTab) ? queryTab : null)) as GdprTab | null;
  const deepRopa = query.get("entry");
  const deepDsr = query.get("request");
  const deepDpia = query.get("record");
  const deepBreach = query.get("breach");
  // The sidebar rows are the only register switcher; plain /gdpr is redirected below.
  const activeTab = pathTab;
  const [dp, setDp] = useState<DpContextData>(DEFAULT_DP_CONTEXT);
  const [ctxLoaded, setCtxLoaded] = useState(false);
  const [baseLinkOptions, setLinkOptions] = useState<Omit<LinkOptionsData, "ropa">>({ suppliers: [], assets: [], risks: [] });
  const [ropaAttention, setRopaAttention] = useState<DpAttentionItem[]>([]);

  const [ropaEntries, setRopaEntries] = useState<RopaEntry[]>([]);
  const [dsrEntries,  setDsrEntries]  = useState<DsrEntry[]>([]);
  const [dpiaEntries, setDpiaEntries] = useState<DpiaEntry[]>([]);
  const [breachEntries, setBreachEntries] = useState<BreachEntry[]>([]);

  const [loadingRopa,    setLoadingRopa]    = useState(true);
  const [loadingDsr,     setLoadingDsr]     = useState(true);
  const [loadingDpia,    setLoadingDpia]    = useState(true);
  const [loadingBreaches, setLoadingBreaches] = useState(true);

  const loadRopa = useCallback(async () => {
    setLoadingRopa(true);
    try {
      const d = await api.get<{ entries: RopaEntry[]; attention?: DpAttentionItem[] }>("/gdpr/ropa");
      setRopaEntries((d.entries ?? []).map((e) => ({ ...e, dpiaId: e.dpia?.id ?? null, dpiaStatus: e.dpia?.status ?? null })));
      setRopaAttention(d.attention ?? []);
    } catch { setRopaEntries([]); setRopaAttention([]); }
    finally { setLoadingRopa(false); }
  }, []);

  const loadDsr = useCallback(async () => {
    setLoadingDsr(true);
    try {
      const d = await api.get<{ requests: DsrEntry[] }>("/gdpr/dsr");
      setDsrEntries(d.requests ?? []);
    } catch { setDsrEntries([]); }
    finally { setLoadingDsr(false); }
  }, []);

  const loadDpia = useCallback(async () => {
    setLoadingDpia(true);
    try {
      const d = await api.get<{ assessments: DpiaEntry[] }>("/gdpr/dpia");
      setDpiaEntries(d.assessments ?? []);
    } catch { setDpiaEntries([]); }
    finally { setLoadingDpia(false); }
  }, []);

  const loadBreaches = useCallback(async () => {
    setLoadingBreaches(true);
    try {
      const d = await api.get<{ breaches: BreachEntry[] }>("/gdpr/breaches");
      setBreachEntries(d.breaches ?? []);
    } catch { setBreachEntries([]); }
    finally { setLoadingBreaches(false); }
  }, []);

  useEffect(() => {
    api.get<DpContextData>("/gdpr/context")
      .then((ctx) => setDp(ctx))
      .catch(() => {})
      .finally(() => setCtxLoaded(true));
    loadRopa();
    loadDsr();
    loadDpia();
    loadBreaches();
    // What the records can link to. A register the member cannot read leaves its picker empty.
    Promise.all([
      api.get<{ suppliers: Array<{ id: string; name: string }> }>("/tprm/suppliers").then((d) => d.suppliers.map((s) => ({ id: s.id, label: s.name }))).catch(() => []),
      api.get<{ assets: Array<{ id: string; name: string }> }>("/resilience/assets").then((d) => d.assets.map((a) => ({ id: a.id, label: a.name }))).catch(() => []),
      api.get<{ risks: Array<{ id: string; title: string; displayId: number | null }> }>("/risks").then((d) => d.risks.map((r) => ({ id: r.id, label: riskLabel(r) }))).catch(() => []),
    ]).then(([suppliers, assets, risks]) => setLinkOptions({ suppliers, assets, risks }));
  }, [loadRopa, loadDsr, loadDpia, loadBreaches]);

  // The processing activities a request or breach can be linked to, from the register itself.
  const linkOptions: LinkOptionsData = { ...baseLinkOptions, ropa: ropaEntries.map((e) => ({ id: e.id, label: e.name })) };

  // Authority-notification overdue count for tab badge
  const authorityHours = dp.clocks.breachAuthority?.value ?? 72;
  const overdueAuthority = breachEntries.filter((b) => isAuthorityOverdue(b, authorityHours)).length;
  const openDsr    = dsrEntries.filter((e) => !["completed", "refused", "withdrawn"].includes(e.status)).length;

  // The register is for the laws that require one — but a register that already
  // holds entries (a supplier flagged as processor, a DPIA's activity) is never hidden.
  const showRopa = dp.ropa.required || ropaEntries.length > 0;

  // Plain /gdpr opens the register that matters — an overdue breach notification, then an
  // open request, then the RoPA where one is kept — at its own address, so its sidebar row
  // is the one highlighted. /gdpr/ropa with no register to keep goes to the request tracker.
  const settled = ctxLoaded && !loadingRopa && !loadingDsr && !loadingBreaches;
  useEffect(() => {
    if (!settled) return;
    if (!pathTab) {
      const target: GdprTab = overdueAuthority > 0 ? "breaches" : openDsr > 0 ? "dsr" : showRopa ? "ropa" : "dsr";
      navigate(`/gdpr/${target}${search}`, { replace: true });
    } else if (pathTab === "ropa" && !showRopa) {
      navigate("/gdpr/dsr", { replace: true });
    }
  }, [settled, pathTab, overdueAuthority, openDsr, showRopa, search, navigate]);
  const title = /gdpr/i.test(dp.module.label) ? `${dp.module.label} Compliance` : dp.module.label;
  const lawList = dp.regulations.length > 0 ? dp.regulations.join(", ") : "the data protection laws that apply to you";

  return (
    <DpContext.Provider value={dp}>
    <CanEdit.Provider value={canEdit}>
    <LinkOptions.Provider value={linkOptions}>
    <div className="page-pad">
      <h1 style={{ ...pageHeading, marginBottom: 6 }}>
        {title}
      </h1>
      <p style={{ ...pageSubtitle, marginBottom: 16 }}>
        Manage your obligations under {lawList}: {showRopa ? "records of processing, " : ""}data subject requests, impact assessments and breach notifications.
      </p>
      <PlaybookBar ids={["personal-data-breach", "subject-access-requests"]} />

      {/* Data protection document templates — generated from the records kept on this page */}
      <div style={{ ...card, display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", marginBottom: 24, flexWrap: "wrap" }}>
        <FileText size={18} style={{ color: "var(--c-accentText)", flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>Data protection documents</div>
          <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>
            Generate your Privacy Notice, Data Subject Rights and Breach procedures, Records of Processing (as an Excel workbook), Retention Schedule, Data Processing Agreement and more — written for the data protection laws that apply to you and pre-populated from the records on this page.
          </div>
        </div>
        <button
          onClick={() => navigate("/templates?filter=data_protection")}
          style={{ ...btnPrimary, padding: "7px 14px", fontSize: 12, whiteSpace: "nowrap" }}
        >
          Open templates
        </button>
      </div>

      {/* No tab row: the registers are rows of their own in the sidebar (Laws & privacy).
          What the tab badges used to flag is said here, wherever the member is. */}
      {((activeTab !== "breaches" && overdueAuthority > 0) || (activeTab !== "dsr" && openDsr > 0)) && (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 20, fontSize: 13 }}>
          {activeTab !== "breaches" && overdueAuthority > 0 && (
            <button type="button" onClick={() => navigate("/gdpr/breaches")} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12, color: "var(--c-dangerText)", background: "var(--c-dangerBg)" }}>
              {overdueAuthority} breach notification{overdueAuthority === 1 ? "" : "s"} to the authority overdue
            </button>
          )}
          {activeTab !== "dsr" && openDsr > 0 && (
            <button type="button" onClick={() => navigate("/gdpr/dsr")} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12, color: "var(--c-warningText)", background: "var(--c-warningBg)" }}>
              {openDsr} open data subject request{openDsr === 1 ? "" : "s"}
            </button>
          )}
        </div>
      )}

      {!activeTab && <div style={{ ...text.body, color: theme.color.muted }}>Loading…</div>}

      {activeTab === "ropa" && showRopa && (
        <RopaTab ropaEntries={ropaEntries} attention={ropaAttention} loading={loadingRopa} reload={loadRopa} initialOpen={deepRopa} />
      )}
      {activeTab === "dsr" && (
        <DsrTab dsrEntries={dsrEntries} loading={loadingDsr} reload={loadDsr} initialOpen={deepDsr} />
      )}
      {activeTab === "dpia" && (
        <DpiaTab dpiaEntries={dpiaEntries} ropaEntries={ropaEntries} loading={loadingDpia} reload={loadDpia} initialOpen={deepDpia} />
      )}
      {activeTab === "breaches" && (
        <BreachesTab breachEntries={breachEntries} loading={loadingBreaches} reload={loadBreaches} initialOpen={deepBreach} />
      )}
      {activeTab === "agreements" && (
        <AgreementsTab canEdit={canEdit} initialOpen={query.get("agreement")} />
      )}
    </div>
    </LinkOptions.Provider>
    </CanEdit.Provider>
    </DpContext.Provider>
  );
}
